import { CalendarIcon, CardIcon, CompanyIcon, GiftCardIcon, InvoiceIcon, MyAccountIcon } from '@/components/icons';

export const BOOK_PATH = '/support/boka-tid';
export const PAYMENT_PATH = '/support/betalning';
export const INVOICE_PATH = '/support/faktura';
export const MY_ACCOUNT_PATH = '/support/mitt-konto';
export const GIFTCARD_PATH = '/support/presentkort';
export const BUSINESS_PATH = '/support/foretag';

export type SupportFormLink = {
  text: string;
  questionId: string;
};

export type Question = {
  id: number;
  question: string;
  answer?: string;
  supportFormLinks?: SupportFormLink[];
  section?: string;
};

export type Section = {
  title: string;
  path?: string;
  icon?: any;
  questions: Question[];
  bgImage?: string;
};

export type FormSection = {
  options: Option[];
  sectionPath: string;
  title?: string;
  icon?: any;
};

export type Option = {
  id: string;
  option: string;
  tag?: number;
  followUpQuestions?: FollowUpQuestion[];
  requiresLogin?: boolean;
  requiresUpload?: boolean;
};

export type ConnectedQuestion = {
  id: string;
  value?: string;
};

export type FollowUpQuestion = {
  id: string;
  question: string;
  type: 'text' | 'checkbox' | 'date';
  values?: Option[];
  validation?: 'phone' | 'email' | 'date' | 'checkbox';
  required?: boolean;
  connected?: ConnectedQuestion;
  placeholder?: string;
};

export const supportFormFaq: FormSection[] = [
  {
    sectionPath: BOOK_PATH,
    options: [
      {
        id: 'missing_booking_confirmation',
        option: 'Jag saknar en bokningsbekräftelse.',
        tag: 7738014,
        followUpQuestions: [
          {
            id: 'company',
            question: 'Vilket företag har du bokat hos?',
            type: 'text',
            placeholder: 'Företag',
          },
          {
            id: 'date',
            question: 'Vilket datum och tid gäller det?',
            type: 'date',
            placeholder: 'Datum (YYYY-MM-DD HH:mm)',
            validation: 'date',
          },
        ],
      },
      {
        id: 'booking_confirmation_not_booked',
        option: 'Jag har fått en bokningsbekräftelse på något som jag inte har bokat.',
        tag: 7738015,
        followUpQuestions: [
          {
            id: 'booking_confirmation_by',
            question: 'Jag fick bokningsbekräftelse via',
            type: 'checkbox',
            validation: 'checkbox',
            values: [
              {
                id: 'email',
                option: 'E-postadress',
              },
              {
                id: 'sms',
                option: 'SMS',
              },
            ],
          },
          {
            id: 'phone',
            question: 'Mitt telefonnummer är',
            type: 'text',
            placeholder: 'Telefonnummer',
            validation: 'phone',
            connected: {
              id: 'booking_confirmation_by',
              value: 'sms',
            },
          },
        ],
      },
      {
        id: 'cannot_cancel_booking',
        option: 'Jag lyckas inte avboka min tid.',
        tag: 7738016,
        followUpQuestions: [
          {
            id: 'company',
            question: 'Vilket företag har du bokat hos?',
            type: 'text',
            placeholder: 'Företag',
          },
          {
            id: 'date',
            question: 'Vilket datum och tid gäller det?',
            type: 'date',
            placeholder: 'Datum (YYYY-MM-DD HH:mm)',
            validation: 'date',
          },
        ],
      },
    ],
  },
  {
    sectionPath: PAYMENT_PATH,
    options: [
      {
        id: 'booking_canceled_refund',
        option: 'Jag har avbokat en tid men inte fått återbetalt för min förskottsbetalning.',
        tag: 7738017,
        requiresLogin: true,
        followUpQuestions: [
          {
            id: 'company',
            question: 'Vilket företag har du bokat hos?',
            type: 'text',
            placeholder: 'Företag',
          },
          {
            id: 'date',
            question: 'Vilket datum och tid gäller det?',
            type: 'date',
            placeholder: 'Datum (YYYY-MM-DD HH:mm)',
            validation: 'date',
          },
          {
            id: 'payment',
            question: 'Hur har du betalat?',
            type: 'text',
            placeholder: 'Betalsätt',
          },
        ],
      },
    ],
  },
  {
    sectionPath: MY_ACCOUNT_PATH,
    options: [
      {
        id: 'extract_stored_data',
        option: 'Jag vill ha ett utdrag på datan ni har sparat om mitt konto.',
        requiresLogin: true,
        tag: 7738022,
      },
      {
        id: 'help_with_login',
        option: 'Jag behöver hjälp med mitt konto',
        tag: 9120958,
        followUpQuestions: [
          {
            id: 'email',
            question: 'Vilken e-postadress loggar du in med?',
            type: 'text',
            placeholder: 'E-postadress',
            validation: 'email',
          },
        ],
      },
    ],
  },
  {
    sectionPath: GIFTCARD_PATH,
    options: [
      {
        id: 'giftcard_not_found',
        option: 'Jag har köpt ett presentkort men hittar det inte.',
        tag: 7738023,
        followUpQuestions: [
          {
            id: 'email',
            question: 'Vilken e-postadress angavs vid köpet?',
            type: 'text',
            placeholder: 'E-postadress',
            validation: 'email',
          },
          {
            id: 'date',
            question: 'När gjordes köpet?',
            type: 'date',
            placeholder: 'Datum (YYYY-MM-DD HH:mm)',
            validation: 'date',
          },
        ],
      },
      {
        id: 'cancel_purchase',
        option: 'Jag vill ångra ett köp av presentkort/värdekort/friskvårdskort/klippkort',
        tag: 7738024,
        followUpQuestions: [
          {
            id: 'email',
            question: 'Vilken e-postadress angavs vid köpet?',
            type: 'text',
            placeholder: 'E-postadress',
            validation: 'email',
          },
          {
            id: 'giftcard_code',
            question: 'Vad är presentkortskoden? (står på presentkortet)',
            type: 'text',
            placeholder: 'Presentkortskoden',
          },
        ],
      },
      {
        id: 'giftcard_not_working',
        option: 'Mitt presentkort fungerar inte.',
        tag: 7738025,
        followUpQuestions: [
          {
            id: 'email',
            question: 'Vilken e-postadress angavs vid köpet?',
            type: 'text',
            placeholder: 'E-postadress',
            validation: 'email',
          },
          {
            id: 'giftcard_code',
            question: 'Vad är presentkortskoden? (står på presentkortet)',
            type: 'text',
            placeholder: 'Presentkortskoden',
          },
        ],
      },
      {
        id: 'send_giftcard_receipt_again',
        option: 'Jag behöver hjälp att hitta mitt kvitto',
        tag: 7738018,
        followUpQuestions: [
          {
            id: 'email',
            question: 'Vilken e-postadress angavs vid köpet?',
            type: 'text',
            placeholder: 'E-postadress',
            validation: 'email',
          },
          {
            id: 'date',
            question: 'När gjordes köpet?',
            type: 'date',
            placeholder: 'Datum (YYYY-MM-DD HH:mm)',
            validation: 'date',
          },
        ],
      },
    ],
  },
  {
    sectionPath: INVOICE_PATH,
    options: [
      {
        id: 'send_docs',
        option: 'Jag har varit i kontakt med er och har blivit ombedd att skicka in underlag.',
        requiresUpload: true,
        tag: 7738028,
      },
    ],
  },
  {
    sectionPath: BUSINESS_PATH,
    options: [
      {
        id: 'update_review',
        option: 'Jag vill ta bort ett gammalt omdöme.',
        tag: 7738026,
        requiresLogin: true,
        followUpQuestions: [
          {
            id: 'company',
            question: 'Vilket företag har du recenserat?',
            type: 'text',
            placeholder: 'Företag',
          },
          {
            id: 'date',
            question: 'När lämnades recensionen?',
            type: 'date',
            placeholder: 'Datum (YYYY-MM-DD HH:mm)',
            validation: 'date',
          },
          {
            id: 'why',
            question: 'Varför vill du ta bort recensionen?',
            type: 'text',
            placeholder: 'Varför',
          },
        ],
      },
    ],
  },
];

export const supportFormFaqEn: FormSection[] = [
  {
    sectionPath: BOOK_PATH,
    options: [
      {
        id: 'missing_booking_confirmation',
        option: "I'm missing a booking confirmation.",
        tag: 7738014,
        followUpQuestions: [
          {
            id: 'company',
            question: 'Which company did you book with?',
            type: 'text',
            placeholder: 'Company',
          },
          {
            id: 'date',
            question: 'What date and time is it regarding?',
            type: 'date',
            placeholder: 'Date (YYYY-MM-DD HH:mm)',
            validation: 'date',
          },
        ],
      },
      {
        id: 'booking_confirmation_not_booked',
        option: "I've received a booking confirmation for something I didn't book.",
        tag: 7738015,
        followUpQuestions: [
          {
            id: 'booking_confirmation_by',
            question: 'I received the booking confirmation via',
            type: 'checkbox',
            validation: 'checkbox',
            values: [
              {
                id: 'email',
                option: 'Email address',
              },
              {
                id: 'sms',
                option: 'SMS',
              },
            ],
          },
          {
            id: 'phone',
            question: 'My phone number is',
            type: 'text',
            placeholder: 'Phone number',
            validation: 'phone',
            connected: {
              id: 'booking_confirmation_by',
              value: 'sms',
            },
          },
        ],
      },
      {
        id: 'cannot_cancel_booking',
        option: "I can't cancel my appointment.",
        tag: 7738016,
        followUpQuestions: [
          {
            id: 'company',
            question: 'Which company did you book with?',
            type: 'text',
            placeholder: 'Company',
          },
          {
            id: 'date',
            question: 'What date and time is it regarding?',
            type: 'date',
            placeholder: 'Date (YYYY-MM-DD HH:mm)',
            validation: 'date',
          },
        ],
      },
    ],
  },
  {
    sectionPath: PAYMENT_PATH,
    options: [
      {
        id: 'booking_canceled_refund',
        option: "I've canceled an appointment but haven't received a refund for my advance payment.",
        tag: 7738017,
        requiresLogin: true,
        followUpQuestions: [
          {
            id: 'company',
            question: 'Which company did you book with?',
            type: 'text',
            placeholder: 'Company',
          },
          {
            id: 'date',
            question: 'What date and time is it regarding?',
            type: 'date',
            placeholder: 'Date (YYYY-MM-DD HH:mm)',
            validation: 'date',
          },
          {
            id: 'payment',
            question: 'How did you pay?',
            type: 'text',
            placeholder: 'Payment method',
          },
        ],
      },
    ],
  },
  {
    sectionPath: MY_ACCOUNT_PATH,
    options: [
      {
        id: 'extract_stored_data',
        option: 'I want a copy of the data you have stored about my account.',
        requiresLogin: true,
        tag: 7738022,
      },
      {
        id: 'help_with_login',
        option: 'I need help with my account',
        tag: 9120958,
        followUpQuestions: [
          {
            id: 'email',
            question: 'Which email address do you use to log in?',
            type: 'text',
            placeholder: 'Email address',
            validation: 'email',
          },
        ],
      },
    ],
  },
  {
    sectionPath: GIFTCARD_PATH,
    options: [
      {
        id: 'giftcard_not_found',
        option: "I've bought a gift card but can't find it.",
        tag: 7738023,
        followUpQuestions: [
          {
            id: 'email',
            question: 'Which email address was used at the time of purchase?',
            type: 'text',
            placeholder: 'Email address',
            validation: 'email',
          },
          {
            id: 'date',
            question: 'When was the purchase made?',
            type: 'date',
            placeholder: 'Date (YYYY-MM-DD HH:mm)',
            validation: 'date',
          },
        ],
      },
      {
        id: 'cancel_purchase',
        option: 'I want to cancel a purchase of a giftcard/valuecard/wellnesscard/bundle',
        tag: 7738024,
        followUpQuestions: [
          {
            id: 'email',
            question: 'Which email address was used at the time of purchase?',
            type: 'text',
            placeholder: 'Email address',
            validation: 'email',
          },
          {
            id: 'giftcard_code',
            question: 'What is the gift card code? (found on the gift card)',
            type: 'text',
            placeholder: 'Gift card code',
          },
        ],
      },
      {
        id: 'giftcard_not_working',
        option: "My gift card doesn't work.",
        tag: 7738025,
        followUpQuestions: [
          {
            id: 'email',
            question: 'Which email address was used at the time of purchase?',
            type: 'text',
            placeholder: 'Email address',
            validation: 'email',
          },
          {
            id: 'giftcard_code',
            question: 'What is the gift card code? (found on the gift card)',
            type: 'text',
            placeholder: 'Gift card code',
          },
        ],
      },
      {
        id: 'send_giftcard_receipt_again',
        option: 'I need help to find my receipt',
        tag: 7738018,
        followUpQuestions: [
          {
            id: 'email',
            question: 'Which email address was used at the time of purchase?',
            type: 'text',
            placeholder: 'Email address',
            validation: 'email',
          },
          {
            id: 'date',
            question: 'When was the purchase made?',
            type: 'date',
            placeholder: 'Date (YYYY-MM-DD HH:mm)',
            validation: 'date',
          },
        ],
      },
    ],
  },
  {
    sectionPath: INVOICE_PATH,
    options: [
      {
        id: 'send_docs',
        option: 'I have been in contact and have been asked to send in documentation.',
        requiresUpload: true,
        tag: 7738028,
      },
    ],
  },
  {
    sectionPath: BUSINESS_PATH,
    options: [
      {
        id: 'update_review',
        option: 'I want to delete an old review.',
        tag: 7738026,
        requiresLogin: true,
        followUpQuestions: [
          {
            id: 'company',
            question: 'Which company did you review?',
            type: 'text',
            placeholder: 'Company',
          },
          {
            id: 'date',
            question: 'When was the review left?',
            type: 'date',
            placeholder: 'Date (YYYY-MM-DD HH:mm)',
            validation: 'date',
          },
          {
            id: 'why',
            question: 'Why do you want to delete the review?',
            type: 'text',
            placeholder: 'Reason',
          },
        ],
      },
    ],
  },
];

export const supportFormFaqFin: FormSection[] = [
  {
    sectionPath: BOOK_PATH,
    options: [
      {
        id: 'missing_booking_confirmation',
        option: 'En ole saanut varausvahvistusta.',
        tag: 7738014,
        followUpQuestions: [
          {
            id: 'company',
            question: 'Minkä yrityksen kautta varasit?',
            type: 'text',
            placeholder: 'Yritys',
          },
          {
            id: 'date',
            question: 'Mitä päivämäärää ja aikaa asia koskee?',
            type: 'date',
            placeholder: 'Päivämäärä (VVVV-KK-PP HH:mm)',
            validation: 'date',
          },
        ],
      },
      {
        id: 'booking_confirmation_not_booked',
        option: 'Olen saanut varausvahvistuksen varauksesta, jota en ole tehnyt.',
        tag: 7738015,
        followUpQuestions: [
          {
            id: 'booking_confirmation_by',
            question: 'Sain varausvahvistuksen',
            type: 'checkbox',
            validation: 'checkbox',
            values: [
              {
                id: 'email',
                option: 'Sähköpostiosoite',
              },
              {
                id: 'sms',
                option: 'SMS',
              },
            ],
          },
          {
            id: 'phone',
            question: 'Puhelinnumeroni on',
            type: 'text',
            placeholder: 'Puhelinnumero',
            validation: 'phone',
            connected: {
              id: 'booking_confirmation_by',
              value: 'sms',
            },
          },
        ],
      },
      {
        id: 'cannot_cancel_booking',
        option: 'En onnistu perumaan aikaani.',
        tag: 7738016,
        followUpQuestions: [
          {
            id: 'company',
            question: 'Minkä yrityksen kautta varasit?',
            type: 'text',
            placeholder: 'Yritys',
          },
          {
            id: 'date',
            question: 'Mitä päivämäärää ja aikaa asia koskee?',
            type: 'date',
            placeholder: 'Päivämäärä (VVVV-KK-PP HH:mm)',
            validation: 'date',
          },
        ],
      },
    ],
  },
  {
    sectionPath: PAYMENT_PATH,
    options: [
      {
        id: 'booking_canceled_refund',
        option: 'Olen perunut ajan, mutta en ole saanut maksun palautusta.',
        tag: 7738017,
        requiresLogin: true,
        followUpQuestions: [
          {
            id: 'company',
            question: 'Minkä yrityksen kautta varasit?',
            type: 'text',
            placeholder: 'Yritys',
          },
          {
            id: 'date',
            question: 'Mitä päivämäärää ja aikaa asia koskee?',
            type: 'date',
            placeholder: 'Päivämäärä (VVVV-KK-PP HH:mm)',
            validation: 'date',
          },
          {
            id: 'payment',
            question: 'Miten maksoit?',
            type: 'text',
            placeholder: 'Maksutapa',
          },
        ],
      },
    ],
  },
  {
    sectionPath: MY_ACCOUNT_PATH,
    options: [
      {
        id: 'extract_stored_data',
        option: 'Haluan saada tiedot tilistäni',
        requiresLogin: true,
        tag: 7738022,
      },
      {
        id: 'help_with_login',
        option: 'Tarvitsen apua tilini kanssa',
        tag: 9120958,
        followUpQuestions: [
          {
            id: 'email',
            question: 'Millä sähköpostiosoitteella kirjaudut sisään?',
            type: 'text',
            placeholder: 'Sähköpostiosoite',
            validation: 'email',
          },
        ],
      },
    ],
  },
  {
    sectionPath: GIFTCARD_PATH,
    options: [
      {
        id: 'giftcard_not_found',
        option: 'Olen ostanut lahjakortin, mutta en löydä sitä.',
        tag: 7738023,
        followUpQuestions: [
          {
            id: 'email',
            question: 'Mikä sähköpostiosoite annettiin oston yhteydessä?',
            type: 'text',
            placeholder: 'Sähköpostiosoite',
            validation: 'email',
          },
          {
            id: 'date',
            question: 'Milloin osto tehtiin?',
            type: 'date',
            placeholder: 'Päivämäärä (VVVV-KK-PP HH:mm)',
            validation: 'date',
          },
        ],
      },
      {
        id: 'cancel_purchase',
        option: 'Haluan perua lahjakortin/arvokortin/hyvinvointikortin/klippikortin oston',
        tag: 7738024,
        followUpQuestions: [
          {
            id: 'email',
            question: 'Mikä sähköpostiosoite annettiin oston yhteydessä?',
            type: 'text',
            placeholder: 'Sähköpostiosoite',
            validation: 'email',
          },
          {
            id: 'giftcard_code',
            question: 'Mikä on lahjakorttikoodi? (löytyy lahjakortista)',
            type: 'text',
            placeholder: 'Lahjakorttikoodi',
          },
        ],
      },
      {
        id: 'giftcard_not_working',
        option: 'Lahjakorttini ei toimi.',
        tag: 7738025,
        followUpQuestions: [
          {
            id: 'email',
            question: 'Mikä sähköpostiosoite annettiin oston yhteydessä?',
            type: 'text',
            placeholder: 'Sähköpostiosoite',
            validation: 'email',
          },
          {
            id: 'giftcard_code',
            question: 'Mikä on lahjakorttikoodi? (löytyy lahjakortista)',
            type: 'text',
            placeholder: 'Lahjakorttikoodi',
          },
        ],
      },
      {
        id: 'send_giftcard_receipt_again',
        option: 'Minun täytyy löytää kuittini',
        tag: 7738018,
        followUpQuestions: [
          {
            id: 'email',
            question: 'Mikä sähköpostiosoite annettiin oston yhteydessä?',
            type: 'text',
            placeholder: 'Sähköpostiosoite',
            validation: 'email',
          },
          {
            id: 'date',
            question: 'Milloin osto tehtiin?',
            type: 'date',
            placeholder: 'Päivämäärä (VVVV-KK-PP HH:mm)',
            validation: 'date',
          },
        ],
      },
    ],
  },
  {
    sectionPath: INVOICE_PATH,
    options: [
      {
        id: 'send_docs',
        option: 'Olen ollut yhteydessä ja minua on pyydetty lähettämään liitteitä.',
        requiresUpload: true,
        tag: 7738028,
      },
    ],
  },
  {
    sectionPath: BUSINESS_PATH,
    options: [
      {
        id: 'update_review',
        option: 'Haluan poistaa vanhan arvostelun',
        tag: 7738026,
        requiresLogin: true,
        followUpQuestions: [
          {
            id: 'company',
            question: 'Minkä yrityksen arvostelit?',
            type: 'text',
            placeholder: 'Yritys',
          },
          {
            id: 'date',
            question: 'Milloin arvostelu jätettiin?',
            type: 'date',
            placeholder: 'Päivämäärä (VVVV-KK-PP HH:mm)',
            validation: 'date',
          },
          {
            id: 'why',
            question: 'Miksi haluat poistaa arvostelun?',
            type: 'text',
            placeholder: 'Syy',
          },
        ],
      },
    ],
  },
];

export const supportFaq: Section[] = [
  {
    title: 'Boka tid',
    icon: CalendarIcon,
    path: BOOK_PATH,
    bgImage: '/images/hero-boka-tid.jpg',
    questions: [
      {
        id: 1,
        question: 'Hur bokar jag?',
        answer: `<p>Använd vår app (<a class="text-link" href="https://bokadirekt.smart.link/cl1p2rx9j">Finns i AppStore och Google Play Butik</a>) eller gå in på Bokadirekt.se och sök tjänsterna som du är intresserad av.
Klicka på ett företag i sökningen så kan du se deras tjänsteutbud, bildgalleri och recensioner.
<br /><br />
Klicka på tjänsten du vill boka och välj tid. Om du är inloggad så behöver du bara bekräfta bokningen. Annars anger du dina uppgifter och bekräftar bokningen.
<br /><br />
Du får en bekräftelse via e-post samt direkt på skärmen.
        </p>`,
      },
      {
        id: 2,
        question: 'Hur avbokar jag?',
        answer: `<p>Genom din inloggning på Bokadirekt så kan du gå till Mina bokningar för att avboka tiden. Vid bokning får du även en bokningsbekräftelse med en avbokningskod. Den kan du ange <a class="text-link" href="https://www.bokadirekt.se/cancel"/>här</a> för att avboka.
<br /><br />
Avbokning kan ske utan kostnad inom reglerna för avbokning.
<br /><br />
Reglerna sätts av varje tjänsteleverantör. Se deras onlinebokning eller din bokningsbekräftelse för att veta vad som gäller för just din bokning.</p>`,
        supportFormLinks: [
          {
            questionId: 'cannot_cancel_booking',
            text: 'Jag lyckas inte avboka min tid.',
          },
        ],
      },
      {
        id: 3,
        question: 'Hur hittar jag min bokningsbekräftelse?',
        answer: `<p>Du får vid slutförd bokning en bekräftelse via e-post samt direkt på skärmen. Saknar du en bokningsbekräftelse så kan du kontakta oss.</p>`,
        supportFormLinks: [
          {
            questionId: 'missing_booking_confirmation',
            text: 'Jag saknar en bokningsbekräftelse.',
          },
        ],
      },
      {
        id: 4,
        question: 'Jag har fått en bekräftelse för något jag inte har bokat.',
        answer: `<p>Kontakta oss gärna via formuläret så ska vi se vad som har hänt.</p>`,
        supportFormLinks: [
          {
            questionId: 'booking_confirmation_not_booked',
            text: 'Jag har fått en bokningsbekräftelse på något som jag inte har bokat.',
          },
        ],
      },
      {
        id: 5,
        question: 'Varför kan jag inte se min bokning som inloggad?',
        answer: `<p>Bokningen kopplas till ditt konto via e-postadressen som är angiven i bokningen. Detta sker per automatik om du är inloggad när du bokar.
<br /><br />
Har du bokat som gäst kommer du inte kunna se bokningen på ditt konto. Har du inte bokat som gäst men du ser fortfarande inte din bokning kontrollera att du är inloggad med rätt e-postadress.
</p>`,
      },
      {
        id: 6,
        question: 'Hur lämnar jag en recension?',
        answer: `<p>Efter en behandling via Bokadirekt kan man få möjligheten att lämna ett omdöme till utövaren. För att man ska få den möjligheten så behöver vissa krav fyllas:<br />
        <ul style="list-style:disc;margin-left: 24px;margin-top:8px">
          <li>Företaget behöver ha funktionen för omdömen aktiverad.</li>
          <li>Bokningen är skapad medan man är inloggad på Bokadirekt, alternativt att företaget själv skapat bokningen och angett din e-postadress i bokningen. Är bokningen skapad som gäst via Bokadirekt kommer man ej få möjligheten att lämna ett omdöme.</li>
          <li>Din e-postadress på bokningen behöver vara korrekt stavad.</li>
          <li>Man får möjligheten att lämna ett omdöme för en utövare en gång under en period på 45 dagar. Så har man redan fått möjligheten behöver 45 dagar passera för att man ska kunna lämna en till för samma utövare. Besöker man samma plats men en annan utövare behöver 14 dagar passera för att man ska få lämna ett omdöme.</li>
          <li>Bokningen får ej vara avbokad.</li>
        </ul>
        <br />
        Fylls dess krav kommer ett e-postmeddelande skickas ut ett par timmar efter behandlingen avslutats, där man kan lämna ett betyg samt en kommentar. Men man kan även lämna omdömet via vår app.
<br /><br />Man har 30 dagar på sig att lämna ett omdöme och det går även att redigera eller ta bort inom dessa 30 dagar.
        </p>`,
      },
    ],
  },
  {
    title: 'Betalning',
    icon: CardIcon,
    path: PAYMENT_PATH,
    bgImage: '/images/hero-betalning.png',
    questions: [
      {
        id: 1,
        question: 'Varför kan jag inte betala min bokning online?',
        answer: `<p>Det är tjänsteutövaren som du bokar hos som väljer vilka betalsätt som erbjuds.
Om tjänsteutövaren erbjuder onlinebetalning och du ändå inte kan betala, så kan det bero på att priset för tjänsten inte visas, eller att den tid du bokar är för långt fram i tiden för att betalas just nu.</p>`,
      },
      {
        id: 2,
        question: 'Jag har betalat min bokning online och avbokat, hur får jag tillbaka mina pengar?',
        answer: `<p>Vid avbokning så avbryts din betalning per automatik.
<br /><br />
Återbetalningstiden beror på betalsätt.
<br /><br />
Vanligtvis tar det 2-3 bankdagar.
<br /><br />
Har du valt en direktbetalning och inte har fått en återbetalning inom 7 bankdagar, vänliga kontakta oss.</p>`,
        supportFormLinks: [
          {
            questionId: 'booking_canceled_refund',
            text: 'Jag har avbokat en tid men inte fått återbetalt för min förskottsbetalning.',
          },
        ],
      },
      {
        id: 3,
        question: 'Kan jag i efterhand betala en bokning som redan är skapad?',
        answer: `<p>Om du loggar in via vår app så kan du gå till dina bokningar och betala dem så länge tjänsteleverantören erbjuder det.</p>`,
      },
      {
        id: 4,
        question: 'Hur hittar jag mitt kvitto?',
        answer: `<p>Vid onlinebetalning så skickas en betalningsbekräftelse efter ditt besök. Slutgiltigt kvitto får du av tjänsteleverantören på plats.
<br /><br />
Saknar du ett kvitto eller önskar du att lägga till dina personuppgifter på kvittot så kan du kontakta tjänsteutövaren.
</p>`,
      },
    ],
  },
  {
    title: 'Mitt konto',
    icon: MyAccountIcon,
    path: MY_ACCOUNT_PATH,
    bgImage: '/images/hero-mitt-konto.png',
    questions: [
      {
        id: 1,
        question: 'Hur återställer jag mitt lösenord?',
        answer: `<p>Numera krävs inget lösenord för att logga in på Bokadirekt. <br />Istället får du ett mail med en verifieringskod när du loggar in, som du sen använder för att komma in på ditt konto.
Får du inget mail så kolla även din skräppostmapp. Du kan även försöka lägga till noreply@bokadirekt.se som en betrodd mailadress/kontakt.
</p>`,
        supportFormLinks: [
          {
            questionId: 'help_with_login',
            text: 'Jag behöver hjälp med mitt konto',
          },
        ],
      },
      {
        id: 2,
        question: 'Hur byter jag e-postadress på min inloggning?',
        answer: `<p>Det går inte att ändra e-postadress för ett befintligt konto.
<br /><br />
Du kan däremot skapa ett nytt konto med din nya e-postadress och fortsätta använda det nya kontot istället. Det gamla kontot kan du ta bort genom att logga in och klicka på Kontoinställningar i menyn.<br />
Klicka sedan på Radera konto.</p>`,
      },
      {
        id: 3,
        question: 'Hur tar jag bort mitt konto?',
        answer: `<p>Du kan ta bort kontot genom att logga in och klicka på <a class="text-link" href="https://www.bokadirekt.se/my-profile#account-settings">Kontoinställningar</a> i menyn. <br />Klicka sedan på Radera konto.</p>`,
      },
      {
        id: 4,
        question: 'Hur hanteras mina uppgifter?',
        answer: `<p>När en kund bokar en tid hos en tjänsteleverantör via Bokadirekt så samlas de personuppgifter tjänsteleverantören anser vara nödvändiga in. Uppgifterna behandlas vidare av Bokadirekt samt tjänsteleverantören under varsitt åtskilt ansvar.
<br /><br />
Du kan läsa mer om vår hantering av personuppgifter:
<a class="text-link" href="https://www.bokadirekt.se/articles/terms-booking-consumer">här</a></p>`,
      },
      {
        id: 5,
        question: 'Jag vill ha ett utdrag på datan ni har sparat om mitt konto.',
        answer: `<p></p>`,
        supportFormLinks: [
          {
            questionId: 'extract_stored_data',
            text: 'Jag vill ha ett utdrag på datan',
          },
        ],
      },
    ],
  },
  {
    title: 'Presentkort & Friskvårdskort',
    icon: GiftCardIcon,
    path: GIFTCARD_PATH,
    bgImage: '/images/hero-presentkort.png',
    questions: [
      {
        id: 1,
        question: 'Hur hittar jag mitt köpta kort?',
        answer: `<p>Vid köp av kort så får du det via e-post direkt vid köpet.
<br /><br />
Vid köp av fysiskt presentkort så postar vi det till den adress som angavs vid köpet.
<br /><br />
Om du inte hittar eller inte har fått ditt presentkort så kan du kontakta oss.</p>`,
        supportFormLinks: [
          {
            questionId: 'giftcard_not_found',
            text: 'Jag har köpt ett presentkort men hittar det inte.',
          },
        ],
      },
      {
        id: 2,
        question: 'Hur bokar jag med mitt köpta kort?',
        answer: `<p>När du skapar en bokning väljer du betalsättet som ditt kort avser, tex Bokadirekt presentkort eller Bokadirekt friskvårdskort. Betalsätten visas endast hos dom tjänsteutövare som accepterar dom korten.
<br /><br />
Tjänster som är godkända för Bokadirekts friskvårdskort är markerade med en symbol som säger “Friskvård”.
        <br /><br />
        Observera att presentkort köpta direkt hos tjänsteleverantören inte går att ange vid bokning, dessa tas istället med till besöket.</p>`,
        supportFormLinks: [
          {
            questionId: 'giftcard_not_working',
            text: 'Mitt presentkort fungerar inte.',
          },
        ],
      },
      {
        id: 3,
        question: 'Mitt presentkorts giltighetstid är passerad',
        answer: `<p>Presentkort köpta på Bokadirekt har 12 månaders giltighetstid från köptillfället.
Du kan se giltighetstid genom att ange koden <a class="text-information-700 underline" href="https://www.bokadirekt.se/giftcards">här</a>
<br /><br />
Giltighetstiden står även angiven på presentkortet.
<br /><br />
Obeservera att Friskvårdskort har en giltighetstid som beror på datumet det köptes. Giltighetstid för friskvårdskort står vid köp och på kvittot.
</p>`,
      },
      {
        id: 4,
        question: 'Hur ser jag saldo för mitt presentkort?',
        answer: `<p>Du kan se ditt presentkorts aktuella saldo genom att ange presentkortskoden (står på presentkortet) <a class="text-information-700 underline" href="https://www.bokadirekt.se/giftcards">här</a></p>`,
      },
      {
        id: 5,
        question: 'Hur hittar jag mitt kvitto?',
        answer: `<p>Kvittot skickas via e-post i samband med köpet. Saknar du ett kvitto kan du söka <a class="text-information-700 underline" href="https://www.bokadirekt.se/giftcards">här</a> på presentkortskoden och välja att skicka om kvittot på nytt. 
Om du råkat ange fel e-postadress vid köpet av ditt kort eller av annan anledning inte får tag på ditt kvitto kan du kontakta oss.</p>`,
        supportFormLinks: [
          {
            questionId: 'send_giftcard_receipt_again',
            text: 'Jag behöver hjälp att hitta mitt kvitto',
          },
        ],
      },
      {
        id: 6,
        question: 'Jag vill ångra ett köp av ett kort',
        answer: `<p>Om det är ett presentkort, friskvårdskort eller värdekort du vill ångra köpet av kan du göra det själv <a class="text-information-700 underline" href="https://www.bokadirekt.se/giftcards">här</a>. Är det ett klippkort du vill ångra, eller av annan anledning behöver hjälp med att ångra ett köp kan du kontakta oss.</p>`,
        supportFormLinks: [
          {
            questionId: 'cancel_purchase',
            text: 'Jag vill ångra ett köp av ett presentkort/värdekort/friskvårdskort/klippkort',
          },
        ],
      },
    ],
  },
  {
    title: 'Faktura',
    icon: InvoiceIcon,
    path: INVOICE_PATH,
    bgImage: '/images/hero-faktura.jpg',
    questions: [
      {
        id: 1,
        question: 'Jag anser att fakturan jag fått från Klarna är felaktig',
        answer: `<p>Du kan bestrida fakturan från Klarna via deras hemsida.
<br /><br />
Läs mer:
<a class="text-link" target="_blank" rel="nofollow external noopener noreferrer" href="https://www.klarna.com/se/beskrivning-av-klarnas-koparskydd/">här</a></p>`,
      },
      {
        id: 2,
        question: 'Jag anser att fakturan jag fått från Qliro är felaktig',
        answer: `<p>Du kan bestrida fakturan från Qliro via deras hemsida.
<br /><br />
Läs mer:
<a class="text-link" target="_blank" rel="nofollow external noopener noreferrer" href="https://se-support.qliro.com/">här</a></p>`,
      },
      {
        id: 3,
        question: 'Jag anser att jag har fått en felaktig no show-faktura',
        answer: `<p>Om du har fått en e-postfaktura från Bokadirekt genom Billogram så kan du kontakta oss genom att öppna fakturan och skriva i meddelandefältet.
<br /><br />
Om du har fått en faktura skickad direkt ifrån tjänsteutövaren så behöver du kontakta dem.
Kontaktuppgifter finns vanligtvis på deras onlinebokning.
<br /><br />
Om du har varit i kontakt med oss om en faktura och har blivit ombedd att skicka in underlag, kontakta oss via formuläret här.</p>`,
        supportFormLinks: [
          {
            questionId: 'send_docs',
            text: 'Jag har varit i kontakt med er och har blivit ombedd att skicka in underlag.',
          },
        ],
      },
    ],
  },
  {
    title: 'Tjänsteutövare',
    icon: CompanyIcon,
    path: BUSINESS_PATH,
    bgImage: '/images/hero-foretag.png',
    questions: [
      {
        id: 1,
        question: 'Jag vill rapportera ett företag anslutet till Bokadirekt.se',
        answer: `<p>Vi är givetvis måna om att du får en bra upplevelse när du bokar via oss.
        Kontakta oss via länken <a class="text-link" href="https://form.typeform.com/to/cFOxh0Uy">här</a> om du vill rapportera ett företag som du har bokat hos.
<br /><br />
Tänk dock på att du vid bokning ingår avtal direkt med den som utför tjänsten. Bokadirekt tar således inget ansvar för felaktiga eller uteblivna behandlingar.</p>`,
      },
      {
        id: 2,
        question: 'Hur lämnar jag en recension?',
        answer: `<p>Efter en behandling via Bokadirekt kan man få möjligheten att lämna ett omdöme till utövaren.
        <br />För att man ska få den möjligheten så behöver vissa krav fyllas:
        <ul style="list-style: disc;margin-left: 24px;margin-top: 8px">
        <li>Företaget behöver ha funktionen för omdömen aktiverad.</li>
        <li>Bokningen är skapad medan man är inloggad på Bokadirekt, alternativt att företaget själv skapat bokningen och angett din e-postadress i bokningen. Är bokningen skapad som gäst via Bokadirekt kommer man ej få möjligheten att lämna ett omdöme.</li>
        <li>Din e-postadress på bokningen behöver vara korrekt stavad.</li>
        <li>Man får möjligheten att lämna ett omdöme för en utövare en gång under en period på 45 dagar. Så har man redan fått möjligheten behöver 45 dagar passera för att man ska kunna lämna en till för samma utövare. Besöker man samma plats men en annan utövare behöver 14 dagar passera för att man ska få lämna ett omdöme.</li>
        <li>Bokningen får ej vara avbokad.</li>
        </ul>
        <br />Fylls dess krav kommer ett e-postmeddelande skickas ut ett par timmar efter behandlingen avslutats, där man kan lämna ett betyg samt en kommentar. Men man kan även lämna omdömet via vår app.
        <br />Man har 30 dagar på sig att lämna ett omdöme och det går även att redigera eller ta bort inom dessa 30 dagar.
        </p>`,
      },
      {
        id: 3,
        question: 'Jag vill ändra eller ta bort en recension som jag har lämnat.',
        answer: `<p>
        Du kan själv ändra eller ta bort ett omdöme via appen upp till 30 dagar efter ditt besök. Önskar du ta bort ett omdöme äldre än 30 dagar kan du kontakta oss.
        </p>`,
        supportFormLinks: [
          {
            questionId: 'update_review',
            text: 'Jag vill ta bort ett gammalt omdöme',
          },
        ],
      },
    ],
  },
];

export const supportFaqFin: Section[] = [
  {
    title: 'Varaa aika',
    icon: CalendarIcon,
    path: BOOK_PATH,
    bgImage: '/images/hero-boka-tid.jpg',
    questions: [
      {
        id: 1,
        question: 'Miten varaan ajan?',
        answer: `<p>Käytä sovellustamme (<a class="text-link" href="https://bokadirekt.smart.link/cl1p2rx9j">Saatavilla AppStoressa ja Google Play Kaupassa</a>) tai mene Bokadirekt.se-sivustolle ja etsi haluamiasi palveluita.
Napsauta hakutuloksissa yritystä nähdäksesi heidän palveluvalikoimansa, kuvagallerian ja arvostelut.
<br /><br />
Napsauta haluamaasi palvelua ja valitse aika. Jos olet kirjautunut sisään, sinun tarvitsee vain vahvistaa varaus. Muussa tapauksessa syötä tietosi ja vahvista varaus.
<br /><br />
Saat vahvistuksen sähköpostitse sekä suoraan näytölle.
        </p>`,
      },
      {
        id: 2,
        question: 'Miten perun ajanvarauksen?',
        answer: `<p>Kirjautumalla Bokadirektiin voit siirtyä Omat varaukset -osioon ja perua ajan. Varausvahvistuksen yhteydessä saat myös peruutuskoodin, jonka voit syöttää <a class="text-link" href="https://www.bokadirekt.se/cancel"/>täällä</a> peruuttaaksesi ajan.
<br /><br />
Peruutus on maksuton peruutusehtojen puitteissa.
<br /><br />
Ehdot määrittelee jokainen palveluntarjoaja itse. Katso heidän verkkovarauspalvelustaan tai varausvahvistuksestasi, mitkä ehdot koskevat varaustasi.</p>`,
        supportFormLinks: [
          {
            questionId: 'cannot_cancel_booking',
            text: 'En onnistu perumaan aikaani.',
          },
        ],
      },
      {
        id: 3,
        question: 'Miten löydän varausvahvistukseni?',
        answer: `<p>Kun varaus on tehty, saat vahvistuksen sähköpostitse sekä suoraan näytölle. Jos et löydä vahvistusta, voit ottaa meihin yhteyttä.</p>`,
        supportFormLinks: [
          {
            questionId: 'missing_booking_confirmation',
            text: 'En ole saanut varausvahvistusta.',
          },
        ],
      },
      {
        id: 4,
        question: 'Olen saanut vahvistuksen varauksesta, jota en ole tehnyt.',
        answer: `<p>Ota meihin yhteyttä lomakkeen kautta, niin selvitämme asian.</p>`,
        supportFormLinks: [
          {
            questionId: 'booking_confirmation_not_booked',
            text: 'Olen saanut varausvahvistuksen varauksesta, jota en ole tehnyt.',
          },
        ],
      },
      {
        id: 5,
        question: 'Miksi en näe varaustani kirjautuneena?',
        answer: `<p>Varaus liitetään tiliisi varauksessa ilmoitetun sähköpostiosoitteen perusteella. Tämä tapahtuu automaattisesti, jos olet kirjautunut sisään varauksen yhteydessä.
<br /><br />
Jos varasit vierailijana, et näe varausta tililläsi. Jos et varannut vierailijana, mutta et silti näe varaustasi, tarkista että olet kirjautunut oikealla sähköpostiosoitteella.
</p>`,
      },
      {
        id: 6,
        question: 'Miten jätän arvostelun?',
        answer: `<p>Voit jättää arvostelun palveluntarjoajalle hoidon jälkeen Bokadirektin kautta, jos tietyt ehdot täyttyvät:<br />
        <ul style="list-style:disc;margin-left: 24px;margin-top:8px">
          <li>Yrityksellä täytyy olla arvostelutoiminto käytössä.</li>
          <li>Varaus on tehty kirjautuneena Bokadirektiin tai yritys on tehnyt varauksen ja ilmoittanut sähköpostiosoitteesi. Jos varaus on tehty vierailijana, et voi jättää arvostelua.</li>
          <li>Sähköpostiosoitteen täytyy olla oikein kirjoitettu.</li>
          <li>Voit jättää arvostelun samalle palveluntarjoajalle kerran 45 päivän aikana. Jos olet jo saanut mahdollisuuden, tulee odottaa 45 päivää. Jos käyt samalla paikalla mutta eri palveluntarjoajalla, tulee odottaa 14 päivää.</li>
          <li>Varausta ei saa olla peruttu.</li>
        </ul>
        <br />
        Jos ehdot täyttyvät, saat sähköpostin muutaman tunnin kuluttua hoidosta, jossa voit jättää arvostelun ja kommentin. Voit myös jättää arvostelun sovelluksen kautta.
<br /><br />Sinulla on 30 päivää aikaa jättää arvostelu ja voit muokata tai poistaa sen tämän ajan sisällä.
        </p>`,
      },
    ],
  },
  {
    title: 'Maksu',
    icon: CardIcon,
    path: PAYMENT_PATH,
    bgImage: '/images/hero-betalning.png',
    questions: [
      {
        id: 1,
        question: 'Miksi en voi maksaa varaustani verkossa?',
        answer: `<p>Palveluntarjoaja päättää, mitä maksutapoja on tarjolla.
Jos palveluntarjoaja tarjoaa verkkomaksun, mutta et silti voi maksaa, syynä voi olla, ettei palvelun hintaa näytetä tai varattu aika on liian kaukana tulevaisuudessa.</p>`,
      },
      {
        id: 2,
        question: 'Olen maksanut varaukseni verkossa ja perunut sen, miten saan rahani takaisin?',
        answer: `<p>Peruessasi varauksen maksu palautetaan automaattisesti.
<br /><br />
Palautusaika riippuu maksutavasta.
<br /><br />
Yleensä palautus kestää 2-3 pankkipäivää.
<br /><br />
Jos valitsit suoramaksun etkä ole saanut palautusta 7 pankkipäivän kuluessa, ota yhteyttä meihin.</p>`,
        supportFormLinks: [
          {
            questionId: 'booking_canceled_refund',
            text: 'Olen perunut ajan, mutta en ole saanut maksun palautusta.',
          },
        ],
      },
      {
        id: 3,
        question: 'Voinko maksaa varauksen jälkikäteen?',
        answer: `<p>Kirjautumalla sovellukseen voit siirtyä omiin varauksiisi ja maksaa ne, jos palveluntarjoaja sallii sen.</p>`,
      },
      {
        id: 4,
        question: 'Mistä löydän kuitin?',
        answer: `<p>Verkkomaksun jälkeen saat maksuvahvistuksen sähköpostitse käyntisi jälkeen. Lopullisen kuitin saat palveluntarjoajalta paikan päällä.
<br /><br />
Jos et löydä kuittia tai haluat lisätä tietoja kuittiin, ota yhteyttä palveluntarjoajaan.
</p>`,
      },
    ],
  },
  {
    title: 'Oma tili',
    icon: MyAccountIcon,
    path: MY_ACCOUNT_PATH,
    bgImage: '/images/hero-mitt-konto.png',
    questions: [
      {
        id: 1,
        question: 'Miten palautan salasanani?',
        answer: `<p>Bokadirektiin kirjautumiseen ei enää tarvita salasanaa. <br />Saat sähköpostitse vahvistuskoodin kirjautuessasi, jota käytät tilillesi pääsemiseen.
Jos et saa sähköpostia, tarkista roskapostikansiosi. Voit myös lisätä noreply@bokadirekt.se luotettuihin osoitteisiin.
</p>`,
        supportFormLinks: [
          {
            questionId: 'help_with_login',
            text: 'Tarvitsen apua tilini kanssa',
          },
        ],
      },
      {
        id: 2,
        question: 'Miten vaihdan kirjautumissähköpostini?',
        answer: `<p>Et voi muuttaa sähköpostiosoitetta olemassa olevaan tiliin.
<br /><br />
Voit kuitenkin luoda uuden tilin uudella sähköpostiosoitteella ja jatkaa sen käyttöä. Vanhan tilin voit poistaa kirjautumalla sisään ja valitsemalla Tiliasetukset-valikosta.<br />
Napsauta sitten Poista tili.</p>`,
      },
      {
        id: 3,
        question: 'Miten poistan tilini?',
        answer: `<p>Voit poistaa tilisi kirjautumalla sisään ja napsauttamalla <a class="text-link" href="https://www.bokadirekt.se/my-profile#account-settings">Tiliasetukset</a> valikossa. <br />Napsauta sitten Poista tili.</p>`,
      },
      {
        id: 4,
        question: 'Miten tietojani käsitellään?',
        answer: `<p>Kun asiakas varaa ajan palveluntarjoajalta Bokadirektin kautta, kerätään palveluntarjoajan tarvitsemat henkilötiedot. Tietoja käsittelevät Bokadirekt ja palveluntarjoaja kumpikin omalla vastuullaan.
<br /><br />
Voit lukea lisää tietojenkäsittelystämme:
<a class="text-link" href="https://www.bokadirekt.se/articles/terms-booking-consumer">täältä</a></p>`,
      },
      {
        id: 5,
        question: 'Haluan saada tiedot, joita teillä on tallennettuna tilistäni.',
        answer: `<p></p>`,
        supportFormLinks: [
          {
            questionId: 'extract_stored_data',
            text: 'Haluan saada tiedot tilistäni',
          },
        ],
      },
    ],
  },
  {
    title: 'Lahjakortti & Hyvinvointikortti',
    icon: GiftCardIcon,
    path: GIFTCARD_PATH,
    bgImage: '/images/hero-presentkort.png',
    questions: [
      {
        id: 1,
        question: 'Miten löydän ostamani kortin?',
        answer: `<p>Oston yhteydessä saat kortin sähköpostitse heti.
<br /><br />
Jos ostit fyysisen lahjakortin, postitamme sen ostossa ilmoitettuun osoitteeseen.
<br /><br />
Jos et löydä tai et ole saanut lahjakorttia, voit ottaa meihin yhteyttä.</p>`,
        supportFormLinks: [
          {
            questionId: 'giftcard_not_found',
            text: 'Olen ostanut lahjakortin, mutta en löydä sitä.',
          },
        ],
      },
      {
        id: 2,
        question: 'Miten varaan ajan ostamallani kortilla?',
        answer: `<p>Kun teet varauksen, valitse maksutavaksi korttisi mukainen vaihtoehto, esim. Bokadirekt-lahjakortti tai Bokadirekt-hyvinvointikortti. Maksutavat näkyvät vain niillä palveluntarjoajilla, jotka hyväksyvät kyseiset kortit.
<br /><br />
Hyvinvointikortilla hyväksytyt palvelut on merkitty "Hyvinvointi"-symbolilla.
        <br /><br />
        Huomioi, että suoraan palveluntarjoajalta ostettuja lahjakortteja ei voi käyttää varauksen yhteydessä, vaan ne otetaan mukaan käynnille.</p>`,
        supportFormLinks: [
          {
            questionId: 'giftcard_not_working',
            text: 'Lahjakorttini ei toimi.',
          },
        ],
      },
      {
        id: 3,
        question: 'Lahjakorttini voimassaoloaika on umpeutunut',
        answer: `<p>Bokadirektista ostetuilla lahjakorteilla on 12 kuukauden voimassaoloaika ostopäivästä.
Voit tarkistaa voimassaoloajan syöttämällä koodin <a class="text-information-700 underline" href="https://www.bokadirekt.se/giftcards">täällä</a>
<br /><br />
Voimassaoloaika on myös merkitty lahjakorttiin.
<br /><br />
Huomioi, että hyvinvointikortin voimassaoloaika riippuu ostopäivästä. Voimassaoloaika näkyy ostossa ja kuitissa.
</p>`,
      },
      {
        id: 4,
        question: 'Miten tarkistan lahjakorttini saldon?',
        answer: `<p>Voit tarkistaa lahjakorttisi saldon syöttämällä lahjakorttikoodin (löytyy kortista) <a class="text-information-700 underline" href="https://www.bokadirekt.se/giftcards">täällä</a></p>`,
      },
      {
        id: 5,
        question: 'Miten löydän kuitin?',
        answer: `<p>Kuitti lähetetään sähköpostitse oston yhteydessä. Jos kuitti puuttuu, voit etsiä lahjakorttikoodin <a class="text-information-700 underline" href="https://www.bokadirekt.se/giftcards">täältä</a> ja valita kuitin uudelleenlähetyksen. 
Jos annoit väärän sähköpostiosoitteen korttia ostaessasi tai et muusta syystä saa kuittia, voit ottaa meihin yhteyttä.</p>`,
        supportFormLinks: [
          {
            questionId: 'send_giftcard_receipt_again',
            text: 'Tarvitsen apua kuitin löytämisessä',
          },
        ],
      },
      {
        id: 6,
        question: 'Haluan peruuttaa korttioston',
        answer: `<p>Jos haluat peruuttaa lahjakortin, liikuntaedun tai arvolahjakortin oston, voit tehdä sen itse <a class="text-information-700 underline" href="https://www.bokadirekt.se/giftcards">täällä</a>. Jos haluat peruuttaa sarjakortin tai tarvitset muusta syystä apua oston peruuttamisessa, voit ottaa meihin yhteyttä.</p>`,
        supportFormLinks: [
          {
            questionId: 'cancel_purchase',
            text: 'Haluan peruuttaa lahjakortin/arvolahjakortin/liikuntaedun/sarjakortin oston',
          },
        ],
      },
    ],
  },
  {
    title: 'Lasku',
    icon: InvoiceIcon,
    path: INVOICE_PATH,
    bgImage: '/images/hero-faktura.jpg',
    questions: [
      {
        id: 1,
        question: 'Mielestäni saamani Klarna-lasku on virheellinen',
        answer: `<p>Voit riitauttaa Klarnan laskun heidän verkkosivuillaan.
<br /><br />
Lue lisää:
<a class="text-link" target="_blank" rel="nofollow external noopener noreferrer" href="https://www.klarna.com/se/beskrivning-av-klarnas-koparskydd/">täältä</a></p>`,
      },
      {
        id: 2,
        question: 'Mielestäni saamani Qliro-lasku on virheellinen',
        answer: `<p>Voit riitauttaa Qlironn laskun heidän verkkosivuillaan.
<br /><br />
Lue lisää:
<a class="text-link" target="_blank" rel="nofollow external noopener noreferrer" href="https://se-support.qliro.com/">täältä</a></p>`,
      },
      {
        id: 3,
        question: 'Mielestäni olen saanut virheellisen no show -laskun',
        answer: `<p>Jos olet saanut Bokadirektin kautta Billogramilla lähetetyn sähköpostilaskun, voit ottaa meihin yhteyttä avaamalla laskun ja kirjoittamalla viestikenttään.
<br /><br />
Jos olet saanut laskun suoraan palveluntarjoajalta, ota yhteyttä heihin.
Yhteystiedot löytyvät yleensä heidän verkkovarauspalvelustaan.
<br /><br />
Jos olet ollut meihin yhteydessä laskusta ja sinua on pyydetty lähettämään liitteitä, ota meihin yhteyttä lomakkeen kautta täällä.</p>`,
        supportFormLinks: [
          {
            questionId: 'send_docs',
            text: 'Olen ollut yhteydessä ja minua on pyydetty lähettämään liitteitä.',
          },
        ],
      },
    ],
  },
  {
    title: 'Palveluntarjoaja',
    icon: CompanyIcon,
    path: BUSINESS_PATH,
    bgImage: '/images/hero-foretag.png',
    questions: [
      {
        id: 1,
        question: 'Haluan ilmoittaa Bokadirekt.se-palveluun liitetystä yrityksestä',
        answer: `<p>Haluamme tietenkin varmistaa, että saat hyvän kokemuksen varatessasi kauttamme.
        Ota meihin yhteyttä <a class="text-link" href="https://form.typeform.com/to/cFOxh0Uy">täällä</a>, jos haluat ilmoittaa yrityksestä, jonka luona olet käynyt.
<br /><br />
Huomioithan, että varauksen yhteydessä teet sopimuksen suoraan palveluntarjoajan kanssa. Bokadirekt ei vastaa virheellisistä tai toteutumattomista hoidoista.</p>`,
      },
      {
        id: 2,
        question: 'Miten jätän arvostelun?',
        answer: `<p>Voit jättää arvostelun palveluntarjoajalle hoidon jälkeen Bokadirektin kautta, jos tietyt ehdot täyttyvät:
        <ul style="list-style: disc;margin-left: 24px;margin-top: 8px">
        <li>Yrityksellä täytyy olla arvostelutoiminto käytössä.</li>
        <li>Varaus on tehty kirjautuneena Bokadirektiin tai yritys on tehnyt varauksen ja ilmoittanut sähköpostiosoitteesi. Jos varaus on tehty vierailijana, et voi jättää arvostelua.</li>
        <li>Sähköpostiosoitteen täytyy olla oikein kirjoitettu.</li>
        <li>Voit jättää arvostelun samalle palveluntarjoajalle kerran 45 päivän aikana. Jos olet jo saanut mahdollisuuden, tulee odottaa 45 päivää. Jos käyt samalla paikalla mutta eri palveluntarjoajalla, tulee odottaa 14 päivää.</li>
        <li>Varausta ei saa olla peruttu.</li>
        </ul>
        <br />Jos ehdot täyttyvät, saat sähköpostin muutaman tunnin kuluttua hoidosta, jossa voit jättää arvostelun ja kommentin. Voit myös jättää arvostelun sovelluksen kautta.
        <br />Sinulla on 30 päivää aikaa jättää arvostelu ja voit muokata tai poistaa sen tämän ajan sisällä.
        </p>`,
      },
      {
        id: 3,
        question: 'Haluan muuttaa tai poistaa jättämäni arvostelun.',
        answer: `<p>
        Voit itse muuttaa tai poistaa arvostelun sovelluksessa 30 päivän ajan käynnistäsi. Jos haluat poistaa yli 30 päivää vanhan arvostelun, ota meihin yhteyttä.
        </p>`,
        supportFormLinks: [
          {
            questionId: 'update_review',
            text: 'Haluan poistaa vanhan arvostelun',
          },
        ],
      },
    ],
  },
];

export const supportFaqEn: Section[] = [
  {
    title: 'Book an appointment',
    icon: CalendarIcon,
    path: BOOK_PATH,
    bgImage: '/images/hero-boka-tid.jpg',
    questions: [
      {
        id: 1,
        question: 'How do I book?',
        answer: `<p>Use our app (<a class="text-link" href="https://bokadirekt.smart.link/cl1p2rx9j">Available in AppStore and Google Play Store</a>) or go to Bokadirekt.se and search for the services you are interested in.
Click on a company in the search results to see their range of services, photo gallery, and reviews.
<br /><br />
Click on the service you want to book and choose a time. If you are logged in, you just need to confirm the booking. Otherwise, enter your details and confirm the booking.
<br /><br />
You will receive a confirmation via email and directly on the screen.
        </p>`,
      },
      {
        id: 2,
        question: 'How do I cancel my booking?',
        answer: `<p>By logging in to Bokadirekt, you can go to My bookings to cancel your appointment. When booking, you will also receive a booking confirmation with a cancellation code. You can enter it <a class="text-link" href="https://www.bokadirekt.se/cancel"/>here</a> to cancel.
<br /><br />
Cancellation can be made free of charge within the cancellation rules.
<br /><br />
The rules are set by each service provider. See their online booking or your booking confirmation to know what applies to your booking.</p>`,
        supportFormLinks: [
          {
            questionId: 'cannot_cancel_booking',
            text: "I can't cancel my appointment.",
          },
        ],
      },
      {
        id: 3,
        question: 'How do I find my booking confirmation?',
        answer: `<p>When your booking is completed, you will receive a confirmation via email and directly on the screen. If you are missing a booking confirmation, you can contact us.</p>`,
        supportFormLinks: [
          {
            questionId: 'missing_booking_confirmation',
            text: "I'm missing a booking confirmation.",
          },
        ],
      },
      {
        id: 4,
        question: 'I have received a confirmation for something I did not book.',
        answer: `<p>Please contact us via the form and we will investigate what has happened.</p>`,
        supportFormLinks: [
          {
            questionId: 'booking_confirmation_not_booked',
            text: "I've received a booking confirmation for something I didn't book.",
          },
        ],
      },
      {
        id: 5,
        question: "Why can't I see my booking when logged in?",
        answer: `<p>The booking is linked to your account via the email address provided in the booking. This happens automatically if you are logged in when booking.
<br /><br />
If you booked as a guest, you will not be able to see the booking on your account. If you did not book as a guest but still can't see your booking, check that you are logged in with the correct email address.
</p>`,
      },
      {
        id: 6,
        question: 'How do I leave a review?',
        answer: `<p>After a treatment via Bokadirekt, you may have the opportunity to leave a review for the provider. To be able to do so, certain requirements must be met:<br />
        <ul style="list-style:disc;margin-left: 24px;margin-top:8px">
          <li>The company must have the review function enabled.</li>
          <li>The booking is made while you are logged in to Bokadirekt, or the company has created the booking and entered your email address. If the booking is made as a guest via Bokadirekt, you will not be able to leave a review.</li>
          <li>Your email address in the booking must be spelled correctly.</li>
          <li>You can leave a review for a provider once every 45 days. If you have already had the opportunity, 45 days must pass before you can leave another for the same provider. If you visit the same place but a different provider, 14 days must pass before you can leave a review.</li>
          <li>The booking must not be canceled.</li>
        </ul>
        <br />
        If these requirements are met, an email will be sent a few hours after the treatment, where you can leave a rating and a comment. You can also leave the review via our app.
<br /><br />You have 30 days to leave a review, and you can also edit or delete it within these 30 days.
        </p>`,
      },
    ],
  },
  {
    title: 'Payment',
    icon: CardIcon,
    path: PAYMENT_PATH,
    bgImage: '/images/hero-betalning.png',
    questions: [
      {
        id: 1,
        question: "Why can't I pay for my booking online?",
        answer: `<p>The service provider you book with chooses which payment methods are offered.
If the provider offers online payment and you still can't pay, it may be because the price for the service is not displayed, or the time you are booking is too far in the future to be paid for now.</p>`,
      },
      {
        id: 2,
        question: 'I have paid for my booking online and canceled, how do I get my money back?',
        answer: `<p>When you cancel, your payment is automatically canceled.
<br /><br />
The refund time depends on the payment method.
<br /><br />
It usually takes 2-3 banking days.
<br /><br />
If you chose direct payment and have not received a refund within 7 banking days, please contact us.</p>`,
        supportFormLinks: [
          {
            questionId: 'booking_canceled_refund',
            text: "I've canceled an appointment but haven't received a refund for my advance payment.",
          },
        ],
      },
      {
        id: 3,
        question: 'Can I pay for a booking afterwards?',
        answer: `<p>If you log in via our app, you can go to your bookings and pay for them as long as the provider offers it.</p>`,
      },
      {
        id: 4,
        question: 'How do I find my receipt?',
        answer: `<p>For online payments, a payment confirmation is sent after your visit. The final receipt is provided by the service provider on site.
<br /><br />
If you are missing a receipt or want to add your personal details to the receipt, contact the provider.
</p>`,
      },
    ],
  },
  {
    title: 'My account',
    icon: MyAccountIcon,
    path: MY_ACCOUNT_PATH,
    bgImage: '/images/hero-mitt-konto.png',
    questions: [
      {
        id: 1,
        question: 'How do I reset my password?',
        answer: `<p>No password is required to log in to Bokadirekt anymore. <br />Instead, you will receive an email with a verification code when you log in, which you then use to access your account.
If you don't receive an email, also check your spam folder. You can also try adding noreply@bokadirekt.se as a trusted contact.
</p>`,
        supportFormLinks: [
          {
            questionId: 'help_with_login',
            text: 'I need help with my account',
          },
        ],
      },
      {
        id: 2,
        question: 'How do I change the email address for my login?',
        answer: `<p>It is not possible to change the email address for an existing account.
<br /><br />
However, you can create a new account with your new email address and continue using the new account instead. You can delete the old account by logging in and clicking on Account settings in the menu.<br />
Then click on Delete account.</p>`,
      },
      {
        id: 3,
        question: 'How do I delete my account?',
        answer: `<p>You can delete your account by logging in and clicking on <a class="text-link" href="https://www.bokadirekt.se/my-profile#account-settings">Account settings</a> in the menu. <br />Then click on Delete account.</p>`,
      },
      {
        id: 4,
        question: 'How is my data handled?',
        answer: `<p>When a customer books an appointment with a service provider via Bokadirekt, the personal data the provider deems necessary is collected. The data is further processed by Bokadirekt and the provider, each under their own responsibility.
<br /><br />
You can read more about our data handling:
<a class="text-link" href="https://www.bokadirekt.se/articles/terms-booking-consumer">here</a></p>`,
      },
      {
        id: 5,
        question: 'I want a copy of the data you have stored about my account.',
        answer: `<p></p>`,
        supportFormLinks: [
          {
            questionId: 'extract_stored_data',
            text: 'I want a copy of my data',
          },
        ],
      },
    ],
  },
  {
    title: 'Gift cards & Wellness cards',
    icon: GiftCardIcon,
    path: GIFTCARD_PATH,
    bgImage: '/images/hero-presentkort.png',
    questions: [
      {
        id: 1,
        question: 'How do I find my purchased card?',
        answer: `<p>When you purchase a card, you will receive it by email immediately after purchase.
<br /><br />
If you buy a physical gift card, we will mail it to the address provided at purchase.
<br /><br />
If you can't find or haven't received your gift card, you can contact us.</p>`,
        supportFormLinks: [
          {
            questionId: 'giftcard_not_found',
            text: "I've bought a gift card but can't find it.",
          },
        ],
      },
      {
        id: 2,
        question: 'How do I book with my purchased card?',
        answer: `<p>When making a booking, select the payment method that matches your card, e.g., Bokadirekt gift card or Bokadirekt wellness card. The payment methods are only shown for providers who accept those cards.
<br /><br />
Services approved for Bokadirekt's wellness card are marked with a "Wellness" symbol.
        <br /><br />
        Note that gift cards purchased directly from the provider cannot be used when booking; instead, bring them to your appointment.</p>`,
        supportFormLinks: [
          {
            questionId: 'giftcard_not_working',
            text: "My gift card doesn't work.",
          },
        ],
      },
      {
        id: 3,
        question: 'My gift card has expired',
        answer: `<p>Gift cards purchased on Bokadirekt are valid for 12 months from the date of purchase.
You can check the validity by entering the code <a class="text-information-700 underline" href="https://www.bokadirekt.se/giftcards">here</a>
<br /><br />
The validity period is also stated on the gift card.
<br /><br />
Note that wellness cards have a validity period depending on the purchase date. The validity period for wellness cards is shown at purchase and on the receipt.
</p>`,
      },
      {
        id: 4,
        question: 'How do I check the balance of my gift card?',
        answer: `<p>You can check your gift card's current balance by entering the gift card code (found on the card) <a class="text-information-700 underline" href="https://www.bokadirekt.se/giftcards">here</a></p>`,
      },
      {
        id: 5,
        question: 'How do I find my receipt?',
        answer: `<p>The receipt is sent by email at the time of purchase. If you’re missing a receipt, you can search for the gift card code <a class="text-information-700 underline" href="https://www.bokadirekt.se/giftcards">here</a> and choose to resend the receipt. 
If you entered the wrong email address when purchasing your card, or for any other reason can’t access your receipt, you can contact us.</p>`,
        supportFormLinks: [
          {
            questionId: 'send_giftcard_receipt_again',
            text: 'I need help finding my receipt',
          },
        ],
      },
      {
        id: 6,
        question: 'I want to cancel a card purchase',
        answer: `<p>If it’s a gift card, wellness card, or value card you want to cancel, you can do it yourself <a class="text-information-700 underline" href="https://www.bokadirekt.se/giftcards">here</a>. If it’s a punch card you want to cancel, or if for any other reason you need help cancelling a purchase, you can contact us.</p>`,
        supportFormLinks: [
          {
            questionId: 'cancel_purchase',
            text: 'I want to cancel a purchase of a gift card/value card/wellness card/punch card',
          },
        ],
      },
    ],
  },
  {
    title: 'Invoice',
    icon: InvoiceIcon,
    path: INVOICE_PATH,
    bgImage: '/images/hero-faktura.jpg',
    questions: [
      {
        id: 1,
        question: 'I believe the invoice I received from Klarna is incorrect',
        answer: `<p>You can dispute the invoice from Klarna via their website.
<br /><br />
Read more:
<a class="text-link" target="_blank" rel="nofollow external noopener noreferrer" href="https://www.klarna.com/se/beskrivning-av-klarnas-koparskydd/">here</a></p>`,
      },
      {
        id: 2,
        question: 'I believe the invoice I received from Qliro is incorrect',
        answer: `<p>You can dispute the invoice from Qliro via their website.
<br /><br />
Read more:
<a class="text-link" target="_blank" rel="nofollow external noopener noreferrer" href="https://se-support.qliro.com/">here</a></p>`,
      },
      {
        id: 3,
        question: 'I believe I have received an incorrect no-show invoice',
        answer: `<p>If you have received an email invoice from Bokadirekt via Billogram, you can contact us by opening the invoice and writing in the message field.
<br /><br />
If you have received an invoice sent directly from the provider, you need to contact them.
Contact details are usually available on their online booking.
<br /><br />
If you have been in contact with us about an invoice and have been asked to send in documentation, contact us via the form here.</p>`,
        supportFormLinks: [
          {
            questionId: 'send_docs',
            text: 'I have been in contact and have been asked to send in documentation.',
          },
        ],
      },
    ],
  },
  {
    title: 'Service providers',
    icon: CompanyIcon,
    path: BUSINESS_PATH,
    bgImage: '/images/hero-foretag.png',
    questions: [
      {
        id: 1,
        question: 'I want to report a company connected to Bokadirekt.se',
        answer: `<p>We naturally want you to have a good experience when booking through us.
        Contact us via the link <a class="text-link" href="https://form.typeform.com/to/cFOxh0Uy">here</a> if you want to report a company you have booked with.
<br /><br />
Please note that when booking, you enter into an agreement directly with the service provider. Bokadirekt therefore takes no responsibility for incorrect or missed treatments.</p>`,
      },
      {
        id: 2,
        question: 'How do I leave a review?',
        answer: `<p>After a treatment via Bokadirekt, you may have the opportunity to leave a review for the provider.
        <br />To be able to do so, certain requirements must be met:
        <ul style="list-style: disc;margin-left: 24px;margin-top: 8px">
        <li>The company must have the review function enabled.</li>
        <li>The booking is made while you are logged in to Bokadirekt, or the company has created the booking and entered your email address. If the booking is made as a guest via Bokadirekt, you will not be able to leave a review.</li>
        <li>Your email address in the booking must be spelled correctly.</li>
        <li>You can leave a review for a provider once every 45 days. If you have already had the opportunity, 45 days must pass before you can leave another for the same provider. If you visit the same place but a different provider, 14 days must pass before you can leave a review.</li>
        <li>The booking must not be canceled.</li>
        </ul>
        <br />If these requirements are met, an email will be sent a few hours after the treatment, where you can leave a rating and a comment. You can also leave the review via our app.
        <br />You have 30 days to leave a review, and you can also edit or delete it within these 30 days.
        </p>`,
      },
      {
        id: 3,
        question: 'I want to change or delete a review I have left.',
        answer: `<p>
        You can change or delete a review yourself via the app up to 30 days after your visit. If you want to delete a review older than 30 days, you can contact us.
        </p>`,
        supportFormLinks: [
          {
            questionId: 'update_review',
            text: 'I want to delete an old review',
          },
        ],
      },
    ],
  },
];

export const faq: Section[] = [
  {
    title: 'Vanliga frågor Akupunktur',
    path: '/faq-vanliga-fragor/akupunktur',
    questions: [
      {
        id: 1,
        question: 'Vad är akupunktur?',
        answer: `<p>Akupunktur är en alternativmedicinsk behandlingsform som har sitt ursprung i traditionell kinesisk medicin. Den kinesiska medicinen har en holistisk syn på människan och människokroppen och behandlingarna används för att återställa kroppens egna balans. Idag används akupunktur som behandlingsform även i stora delar av västvärlden. Traditionellt ses akupunktur påverka kroppens meridianer, vilka utgör grunden i den traditionella kinesiska läkekonsten. Vid akupunkturbehandling sticks tunna nålar in i hudens akupunkturpunker och genom att stimulera dessa upplöses blockeringar och obalanser i kroppens nervsystem. Behandlingen upplevs som lugnande och smärtlindrande.
          <br /> <br />
          Under behandlingen placeras tunna nålar i huden i och runt omkring området där patienten upplever smärta. Vanligtvis får nålarna sitta i mellan 20-30 minuter innan de tas bort. I vissa fall kan nålarna även ge stimulering genom att kopplas till en elektrisk apparat, detta kallas elektroakupunktur. Klassisk akupunktur används vid en rad olika medicinska besvär så som exempelvis stress, spänningshuvudvärk, utbrändhet, ångest, muskelsmärta och PMS.
        </p>`,
      },
      {
        id: 2,
        question: 'Varför ska du boka en tid för akupunktur?',
        answer: `<p>
          <ul style="list-style:disc;margin-left: 24px;">
            <li>Akupunkturbehandling kan ge smärtlindring vid spänningshuvudvärk, ischias, förlossningssmärtor och PMS.</li>
            <li>Behandlingen kan även ha en lugnande effekt vid symptom så som stress, ångest och mildare depression.</li>
          </ul>
        </p>`,
      },
    ],
  },
  {
    title: 'Vanliga frågor Fillers',
    path: '/faq-vanliga-fragor/fillers',
    questions: [
      {
        id: 1,
        question: 'Varför gör man en injektionsbehandling?',
        answer: `<p>Man kanske tycker sig ha förlorat volym eller bara vill förbättra någon rynka. Det viktigaste är att det är du själv som vill göra behandlingen, och inte för att någon annan sagt det åt dig.</p>`,
      },
      {
        id: 2,
        question: 'Vilka är de vanligaste missuppfattningarna med botox/fillers/injektioner?',
        answer: `<p>En vanlig missuppfattning är att behandlingen kommer förändra dig till den grad att ingen känner igen dig. Det är sällan syftet. Väldigt många gör tex fillers i läpparna bara för att få tillbaka lite volym, jämna ut läpparna eller för att tillföra läpparna fukt.</p>`,
      },
      {
        id: 3,
        question: 'Vad ska man tänka på innan man gör fillers?',
        answer: `<p>Välj en behandlare som är minst legitimerad sjuksköterska och som är utbildad av estetiska injektionsrådet. Gärna med flera års erfarenhet.
          <br /><br />
          Gå på konsultation innan behandlingen och få en uppfattning om kliniken är seriös.
          <br /><br />
          Ca två veckor innan behandlingen ska du heller inte använda läkemedel som ökar blödningsrisken.
        </p>`,
      },
      {
        id: 4,
        question: 'Vad ska man tänka på efter en behandling?',
        answer: `<p>Träna eller sola inte några dagar efter behandlingen. Undvik också att använda smink samma dag som behandlingen gjorts, och rör så lite som möjligt på det behandlade området.</p>`,
      },
      {
        id: 5,
        question: 'Hur länge “håller” en behandling?',
        answer: `<p>Hur länge en behandling håller är individuellt och beror även på vilket område som behandlats och vilket preparat som använts. Generellt håller fillers ca sex till tio månader. För Botox är hållbarheten cirka tre till sex månader.</p>`,
      },
      {
        id: 6,
        question: 'Finns det några tydliga trender i branschen just nu?',
        answer: `<p>Vi ser en tydlig trend i efterfrågan av naturliga resultat,  det ska inte se sprutat ut.</p>`,
      },
      {
        id: 7,
        question: 'Det finns olika varumärke - vad är skillnaden?',
        answer: `<p>Man ska fråga vad som sprutas in i kroppen och vara noga med sina val. Mest känt och säkrast är Restylane som är en svensk kvalitetsprodukt. Det innehåller väldigt ren hyaluronsyra som inte är blandad med en massa tillsatser.</p>`,
      },
      {
        id: 8,
        question: 'Är det farligt med behandling? Gör det ont? Får man bedövning',
        answer: `<p>Innan man gör en fillerbehandling så informeras man om risker såsom blödning, infektion eller nekros (vävnadsdöd). Nekros är dock väldigt ovanligt bland rutinerade behandlare.
          <br /><br />
          Behandlingen kan i stort sett vara helt smärtfri. Ofta får du bedövningssalva innan en behandling, men alla använder inte det.
        </p>`,
      },
    ],
  },
  {
    title: 'Vanliga frågor kring Fransar',
    path: '/faq-vanliga-fragor/kring-fransar',
    questions: [
      {
        id: 1,
        question: 'Vad skiljer volym- och singelfransar?',
        answer: `<p>Volymfransar
          <br /><br />
          Volymfransar är tekniken för dig som vill se ett tydligt resultat och mer volym i fransarna. Enkelt uttryckt sätter man in ett helt knippe med fransar som ger ett fylligare intryck. Denna tekniken kan ge mer än fyrahundra fransar per öga utöver dina naturliga fransar.
          <br /><br />
          Singelfransar
          <br /><br />
          Medan volymfransar ger mer volym så ger singelfransar en naturligare och längre kontur. Antalet fransar är färre än vid applicering av volymfransar; man brukar räkna med femtio till hundra per öga. Singelfransarnas längd, grovhet och form anpassas efter ditt öga.
        </p>`,
      },
      {
        id: 2,
        question: 'Vad ska man tänka på innan man kommer på en fransförlängning?',
        answer: `<p>Kom i god tid till din fransförlängning och helst osminkad. Är det första gången? Fundera på vad du är ute efter för resultat, du kan t.ex. ta med en bild som illustrerar vad du är ute efter - om det är något väldigt dramatiskt eller en mer naturlig look.</p>`,
      },
      {
        id: 3,
        question: 'Vad ska man tänka på efter man gjort en behandling?',
        answer: `<p>För bästa underhåll så är det jätteviktigt att regelbundet rengöra fransarna med ett fransschampo. Annars så samlas lätt talg, smuts, och bakterier längs med franskanten vilket gör att man kan tappa fransar i förtid och i värsta fall få en ögoninfektion. Du bör inte duscha eller tvätta ögonen/ansiktet tolv timmar efter en förlängning eller påfyllnad pga limmets härdningstid.
          <br /><br />
          Försök att inte “sova på ansiktet” använd inte olja i närheten av fransarna.
        </p>`,
      },
      {
        id: 4,
        question: 'Hur länge “håller” en behandling?',
        answer: `<p>Hållbarheten efter en behandling varierar beroende på hur man tar hand om sina fransar. Men det vanligaste är att man kommer för påfyllning var tredje vecka. Fransförlängningen påverkas även negativt av hormoner, graviditet och stress.</p>`,
      },
      {
        id: 5,
        question: 'Kan man göra något för att underhålla fransarna efter en behandling?',
        answer: `<p>Gör rent fransarna dagligen med ett anpassat fransschampo och borste</p>`,
      },
      {
        id: 6,
        question: 'Kan man sminka sig som vanligt efter?',
        answer: `<p>Man kan absolut sminka sig som vanligt efter en behandling men använd inte mascara på fransförlängningen.</p>`,
      },
      {
        id: 7,
        question: 'Vad är trendigt i branschen just nu?',
        answer: `<p>Trenderna i branschen varierar i olika städer men fler vill ha en mer naturlig look än tidigare. Mix och lätt volym går bäst. Att jobba med olika längder och olika böj i ett sett är också extra trendigt.</p>`,
      },
    ],
  },
  {
    title: 'Vanliga frågor kring Hudvård',
    path: '/faq-vanliga-fragor/kring-hudvard',
    questions: [
      {
        id: 1,
        question: 'Varför ska du boka en tid för hudvård/ansiktsbehandling?',
        answer: `<p>
          <ul style="list-style:disc;margin-left: 24px;">
            <li>På sikt minskar vår cellförnyelse och produktion av kollagen och elastin, samtidigt bryts vår naturliga hyaluronsyra i huden ned. För att bibehålla hudens spänst och elasticitet är god hudvård viktigt.</li>
            <li>Ansiktsbehandlingar kan anpassas till en mängd olika hudtyper, behov och problemområden och hjälper dig till en frisk och välmående hud.</li>
            <li>Regelbundna ansiktsbehandlingar med rätt produkter och näringsämnen kan hjälpa dig till de bästa resultaten för just din hud.</li>
          </ul>
        </p>`,
      },
      {
        id: 2,
        question: 'Vad menas med en klassisk ansiktsbehandling?',
        answer: `<p>Den klassiska ansiktsbehandlingen är en omfattande behandling som skräddarsys efter dina behov. Behandlingen innefattar djuprengöring, ånga, portömning och peeling följt av ansiktsmask och ansiktsmassage. Vid behandlingen används produkter anpassade utifrån dina önskemål och din hudtyp. Problemområden att arbeta med kan vara exempelvis torr/fet hud, acne eller rosasea.</p>`,
      },
      {
        id: 3,
        question: 'Vad är ansiktsbehandling express?',
        answer: `<p>För dig som har ont om tid men vill uppnå snabbt resultat och känslan av en rengjord hud med ny lyster, perfekt inför helgen eller kommande festligheter! Behandlingen innefattar lättare rengöring med ånga, kortare ansiktsmassage samt ansiktsmask och produkter anpassade för just din hudtyp. </p>`,
      },
      {
        id: 4,
        question: 'Vad är anti age?',
        answer: `<p>En behandling framtagen specifikt för mogen hy. När vi åldras förlorar vår hud sin spänst och elasticitet, cellförnyelsen går långsammare och vårt kollagen bryts ned. Vid en anti age-behandling används produkter framtagna för den åldrade huden, produkterna innehåller ämnen som stimulerar cellbildningen såsom hyaluronsyra som återfuktar och ökar huden elasticitet samt probiotika som bekämpar åldersprocesser. Behandlingen avslutas med en anti age mask och fuktgivande serum för lyster och fuktgivande effekt.</p>`,
      },
      {
        id: 5,
        question: 'Vad är dermapen nålbehandling?',
        answer: `<p>Dermapen/Micro-needling är en ny och revolutionerande nålbehandling som stimulerar hudens produktion av kollagen. Genom att använda sig av nålbehandling kan näringsrika produkter med aktiva ingredienser nå djupare in i huden för att uppnå en föryngrande effekt. Behandlingen används även på problemområden såsom rynkor, stora porer, solskadad hud eller på synliga ärr i ansiktet. Behandlingen kan även göras tillsammans med en s.k. skinbooster med hyaluronsyra som ger huden en riktig vitamininjektion och ny lyster!</p>`,
      },
      {
        id: 6,
        question: 'Vad är kemisk peeling?',
        answer: `<p>Kemisk peeling är ett samlingsnamn på behandlingar där en blandning av syror används för att uppnå önskat resultat, t.ex. Aha eller Tca syror. Denna behandlingsform är effektiv för att arbeta med problemområden såsom ärr, grov hud, finnar eller pigmenteringar, men kan även användas som enstaka behandling för extra uppfräschning. För extra lyster avslutas behandlingar ofta med en ansiktsmask innehållande såväl vitaminer som antioxidanter.</p>`,
      },
    ],
  },
  {
    title: 'Vanliga frågor kring Klippning',
    path: '/faq-vanliga-fragor/kring-klippning',
    questions: [
      {
        id: 1,
        question: 'Vilka typer av behandlingar är vanligast?',
        answer: `<p>Det går alltid starka trender inom frisyrer. Vilken frisyr passar just dig? Det är alltid bra att förbereda ditt besök hos frisören med att titta på inspirationsbilder på nätet eller i tidningar för att sedan visa för frisören. Då är det enklare för dig att beskriva hur du vill ha det och enklare för frisören att förstå hur just du vill ha din frisyr.</p>`,
      },
      {
        id: 2,
        question: 'Klippning kort hår',
        answer: `<p>Klippning av kort hår den vanligaste tjänsten för män. Vilken teknik frisören använder sig av är väldigt olika. Vissa klipper enbart med sax medan andra använder sig av maskin. Prata med din frisör innan besöket om vilken metod just du vill ha.</p>`,
      },
      {
        id: 3,
        question: 'Klippning långt hår',
        answer: `<p>Det är mer tidskrävande att klippa långt hår och oftast kombineras det med en annan behandling såsom toning eller slingor. I år är trenden att klippa så att du inte ska behöva styla så mycket själv. Vi ser också att trenden går mot ett mer androgynt mode.</p>`,
      },
      {
        id: 4,
        question: 'Tvätta håret hos frisören',
        answer: `<p>Även om du redan har tvättat ditt hår hemma eller på gymmet så finns det inget skönare än att få håret tvättat hos frisören. Många frisörer erbjuder även en hårbottenmassage, vilket är helt underbart.</p>`,
      },
      {
        id: 5,
        question: 'Hur ofta ska jag klippa mig?',
        answer: `<p>Hur ofta du ska klippa dig beror på din håkvalitét och frisyr. En kort frisyr behöver i regel klippas ca var 3-8 vecka för att bibehålla önskad längd och form. Ett långt och friskt hår räcker att klippa var tredje månad medan ett torrt och slitet hår behöver klippas oftare. Rådgör med din frisör vad som passar just din hårtyp och frisyr.</p>`,
      },
      {
        id: 6,
        question: 'Hur ska jag välja frisör?',
        answer: `<p>De flesta av oss söker en frisör som förstår hur man vill ha sin frisyr, som man trivs bra med och som finns i närheten av ens bostad eller arbete. På bokadirekt.se kan du både hitta- och boka frisörer i närheten av dig och ta del av betyg och recensioner från tidigare kunder innan ditt besök.</p>`,
      },
      {
        id: 7,
        question: 'Hur ska jag behandla håret mellan besöken till frisören?',
        answer: `<p>För att bibehålla ett friskt hår mellan frisörbesöken gäller det att ge håret rätt förutsättningar. Använd ett schampo och balsam anpassat för din hårtyp, lägg en hårinpackning ett par gånger i månaden för extra fukt och använd skonsamma stylingverktyg. Prata med din frisör om du är osäker på vilka produkter som passar just dig.</p>`,
      },
      {
        id: 8,
        question: 'Vilka trender är hetast just nu?',
        answer: `<p>I linje med pandemiåret 2020 har hårtrenderna kantats av lättskötta frisyrer och lösa uppsättningar. Nu spås istället många nya spännande frisyrer bli populära. Lockar, lugg i olika former, vågade hårfärger och androgyna herrfrisyrer kommer vi få se mer av 2021. </p>`,
      },
    ],
  },
  {
    title: 'Vanliga frågor Kiropraktik',
    path: '/faq-vanliga-fragor/kiropraktik',
    questions: [
      {
        id: 1,
        question: 'Vad är Kiropraktik?',
        answer: `<p>Kiropraktisk behandling är en justerande behandlingsform som i första hand används för att behandla och förebygga problem kopplade till ryggraden. Problem kopplade till ryggen kan ge symptom som bl.a. spänningar/låsningar i axelpartiet, snedbelastning, ryggskott, ischias, nacksmärtor och ryggvärk. En kiropraktor arbetar för att återställa en normal funktion i patientens leder och muskler.
          <br /><br />
          Vid behandlingen undersöks ryggradens leder och muskler för att kunna diagnosticera spänningar och eventuella skador. Den klassiska behandlingen genomförs manuellt med händerna men även medicinsk akupunktur och stötvågsbehandling kan användas som komplement.
          <br /><br />
          Efter behandling ges oftast en anpassad rehabiliteringsplan för att patienten själv ska kunna arbeta förebyggande och långsiktigt med sina besvär. En kiropraktor kan även ge råd om kost, kosttillskott och vardagsmotion som kan ge ytterligare hjälp till bättre välmående.
        </p>`,
      },
      {
        id: 2,
        question: 'Används kiropraktisk behandling vid idrottsskador?',
        answer: `<p>Ja, kiropraktisk behandling kan även användas vid idrottsskador, både vid akuta skador såsom ryggskott eller vid mer långvariga belastningsskador som uppkommit av t.ex. en snedhet i ryggen. Kiropraktisk behandling kan även användas för att förebygga skador och höja därmed höja din prestationsnivå. Exempel på skador och problem som ofta behandlas med kiropraktik är tennisarmbåge, löparknä, gubbvad och muskelbristningar. </p>`,
      },
    ],
  },
  {
    title: 'Vanliga frågor kring massagebehandlingar',
    path: '/faq-vanliga-fragor/kring-massagebehandlingar',
    questions: [
      {
        id: 1,
        question: 'Varför ska du boka en massagetid?',
        answer: `<p>Det finns en rad fördelar med massage.
          <ul style="list-style:disc;margin-left: 24px;">
            <li>En väl genomförd massage lugnar ner nervsystemet och har en avslappnande effekt på kroppen. </li>
            <li>Massage minskar muskelspänningar och oro vilket i förlängningen till och med kan bidra till att motverka depressioner.</li>
            <li>Massage ökar kroppens blodcirkulation och syresätter kroppens celler</li>
          </ul>
        </p>`,
      },
      {
        id: 2,
        question: 'Vad är klassisk svensk massage?',
        answer: `<p>Den vanligaste massage formen i Sverige är den traditionella “Klassisk svensk massage”. Tekniken bygger på knådning av strykande av hud och muskler. Ibland används apparater i tekniken för att med vibrationer stimulera musklerna. Utöver de generella välgörande egenskaperna från massage så används Klassisk svensk massage i syfte att mjuka upp muskler, mot träningsvärk, för att motverka muskelknutor och för att öka lymfflödet.</p>`,
      },
      {
        id: 3,
        question: 'Vad är hot stone massage?',
        answer: `<p>Hot stone massage är en form av massage där massören använder varma stenar och aromaoljor som verktyg. Just värmen från lavastenarna bidrar till en avslappnande effekt och metoden lämpar sig bäst för avslappning och stressreducering. Mineraler från stenarna som t.ex. järn tas också upp av kroppen med välgörande effekt.</p>`,
      },
      {
        id: 4,
        question: 'Vad är taktil stimulering?',
        answer: `<p>Taktil massage är en varsammare typ av massageterapi där kroppens receptorer berörs med mindre kraft och lättare beröring. Metoden lämpar sig speciellt väl för stressdämpning och för att motverka stress.</p>`,
      },
      {
        id: 5,
        question: 'Vad är bindvävsmassage?',
        answer: `<p>Bindvävsmassage är en en metod för djupgående behandling av muskler. Det är en intensiv terapi som går på djupet för att bryta upp tidigare ärrvävnad  och frigöra bindväv. Rörelserna i bindvävsmassagen är långa och görs tradidionelt utan massageolja eller liknande i syfte att skapa mer friktion och värma upp musklerna.
          <br />
          Tekniken används normalt mer för att motverka muskelproblem och kan vara något mer smärtsam och mindre avslappnande än andra tekniker som mer syftar till avslappning.
        </p>`,
      },
      {
        id: 6,
        question: 'Vad är idrottsmassage?',
        answer: `<p>Idrottsmassage är en behandlande teknik som har sitt ursprung i idrottens värld. Behandlingsformen är utvecklad för att hjälpa idrottares kroppar att återhämta sig. Många tekniker i idrottsmassagen är hämtade från den klassiska massagen men är mer behandlande mot t.ex. tennisarmbåge, benhinneinflamation, sträckningar, spänningshuvudvärk, musarm etc. och är inte av avslappnande karaktär.</p>`,
      },
      {
        id: 7,
        question: 'Vad är thaimassage?',
        answer: `<p>Denna massage, som föga förvånande, har sitt ursprung i Thailand är en uråldrig teknik som kombinerat delar av den klassiska akupressuren och yogan till en behandling. Behandlingsformen är därför bra för att förbättra kroppens rörlighet och även hållning. Traditionellt utförs tekniken på golvet på en matta och omfattar vanligtvis hela kroppen. Massören använder inte bara sina händer utan använder även armbågar, knän etc.
          <br /><br />
          Tekniken är både avslappnande och delvis terapeutisk. Många känner sig både avslappnade och piggare efter en behandling.
        </p>`,
      },
      {
        id: 8,
        question: 'Vad är akupressur?',
        answer: `<p>Akupressuren härstammar från den orientaliska medicinen och är av avslappnande karaktär. Ofta behåller patienten sina kläder under behandlingen och avstressande behandling genom tryck mot känsliga punkter runt nacken, huvudet och axlarna.</p>`,
      },
      {
        id: 9,
        question: 'Vad är gravidmassage?',
        answer: `<p>Gravidmassagen är framtagen för att lindra vanliga smärtor för den gravida kvinnan som problem med rygg, ländrygg och axlar. Behandlingen är både avslappnande och terapeutisk och görs på en bänk med hål för magen eller en för magen speciellt framtagen dyna. Man använder ofta också för gravidmassage anpassad massageolja fri från ämnen som foster kan vara känsliga mot.</p>`,
      },
      {
        id: 10,
        question: 'Vilka trender finns inom massage?',
        answer: `<p><strong>LPG eller Lipo-massage</strong>
          <br /><br />
          LPG massagen är en typ av massage som utförs med en LPG-maskin. Tekniken reducerar fettansamlingar i kroppen och ses därför som ett alternativ till fettsugning och andra kirurgiska ingrepp. Tekniken har också likheter med bindvävsmassagen då den är djupgående och den har även återfuktande egenskaper som gör huden mer elastisk. Under behandlingen tar patienten normalt på sig en typ av kroppsstrumpa som gör att LPG-maskinen glider bättre. Även om det kanske inte låter så så är behandlingen behaglig och lika avslappnande som en klassisk massage.
        </p>`,
      },
    ],
  },
  {
    title: 'Vanliga frågor kring olika behandling för naglar',
    path: '/faq-vanliga-fragor/kring-olika-behandling-for-naglar',
    questions: [
      {
        id: 1,
        question: 'Varför ska du boka en tid för nagelbehandling?',
        answer: `<p>
          <ul style="list-style:disc;margin-left: 24px;">
            <li>Välmående naglar ger vackra händer och ett välvårdat intryck.</li>
            <li>Låt någon ta hand om dina trötta händer och fötter och unna dig själv en avkopplande stund i vardagen. </li>
            <li>Oändliga valmöjligheter, material, form, färg och design, det finns en nagelbehandling för alla!</li>
          </ul>
        </p>`,
      },
      {
        id: 2,
        question: 'Vad är nagelförlängning?',
        answer: `<p>Med en nagelförlängning får dina naglar ett helt nytt utseende. Passar dig med svaga och sköra naglar som vill ha både längre och starkare naglar. Naglarna byggs upp med en tipp som sedan förstärks med material i form av akryl eller gelé. Akryl, som är det starkaste materialet appliceras med pulverteknik där akrylmaterialet härdar av syret i luften. Vid användning av gelé appliceras materialet på nageln och får sedan härda under en UV-lampa. Vid både teknikerna formas naglar till valfri form och målas sedan i valfri färg och design. En nagelförlängning ger ett bestående resultat i upp till 6 veckor.</p>`,
      },
      {
        id: 3,
        question: 'Vad är gellack/shellack?',
        answer: `<p>Gellack/Shellack är en blandning av nagellack och gelé och är en permanent lackning som passar dig med naturligt starka naglar. Lacket som finns i en mängd olika färger och nyanser appliceras på dina egna naglar och stelnar under en UV-ljuslampa. Behandlingen är skonsam och ger ett skyddande och hållbart resultat i upp till 4 veckor. </p>`,
      },
      {
        id: 4,
        question: 'Vad är pedikyr?',
        answer: `<p>En pedikyr innefattar såväl fotbad som en avslappnande underbensmassage och är den perfekta behandlingen för att pigga upp trötta fötter. Förhårdnader på fötterna avlägsnas och hälarna filas, naglarna klipps och filas till valfri längd och form. Pedikyren avslutas med lackning där ett permanent shellack kan användas för ett bestående resultat i upp till 6 veckor. Pedikyren kan även göras som en snabbare express behandling, perfekt för för dig på språng! </p>`,
      },
    ],
  },
];

export const faqEn: Section[] = [
  {
    title: 'Common Questions Acupuncture',
    path: '/faq-vanliga-fragor/akupunktur',
    questions: [
      {
        id: 1,
        question: 'What is acupuncture?',
        answer: `<p>Acupuncture is an alternative medical treatment originating from traditional Chinese medicine. Chinese medicine has a holistic view of humans and the body, and treatments are used to restore the body's own balance. Today, acupuncture is also used as a treatment in much of the Western world. Traditionally, acupuncture is seen to affect the body's meridians, which form the basis of traditional Chinese medicine. During acupuncture treatment, thin needles are inserted into the skin's acupuncture points, and by stimulating these, blockages and imbalances in the body's nervous system are resolved. The treatment is experienced as calming and pain-relieving.
          <br /> <br />
          During the treatment, thin needles are placed in the skin in and around the area where the patient experiences pain. Usually, the needles remain for 20-30 minutes before being removed. In some cases, the needles can also be stimulated by connecting them to an electrical device, called electroacupuncture. Classical acupuncture is used for a variety of medical issues such as stress, tension headaches, burnout, anxiety, muscle pain, and PMS.
        </p>`,
      },
      {
        id: 2,
        question: 'Why should you book an acupuncture appointment?',
        answer: `<p>
          <ul style="list-style:disc;margin-left: 24px;">
            <li>Acupuncture treatment can provide pain relief for tension headaches, sciatica, labor pain, and PMS.</li>
            <li>The treatment can also have a calming effect on symptoms such as stress, anxiety, and mild depression.</li>
          </ul>
        </p>`,
      },
    ],
  },
  {
    title: 'Common Questions Fillers',
    path: '/faq-vanliga-fragor/fillers',
    questions: [
      {
        id: 1,
        question: 'Why do people get injection treatments?',
        answer: `<p>You may feel you have lost volume or just want to improve a wrinkle. The most important thing is that you want the treatment yourself, not because someone else told you to.</p>`,
      },
      {
        id: 2,
        question: 'What are the most common misconceptions about botox/fillers/injections?',
        answer: `<p>A common misconception is that the treatment will change you so much that no one recognizes you. That is rarely the case. Many people get fillers in their lips just to regain some volume, even out the lips, or add moisture.</p>`,
      },
      {
        id: 3,
        question: 'What should you consider before getting fillers?',
        answer: `<p>Choose a practitioner who is at least a licensed nurse and trained by the aesthetic injection council, preferably with several years of experience.
          <br /><br />
          Have a consultation before the treatment to get an idea if the clinic is reputable.
          <br /><br />
          About two weeks before the treatment, you should also not use medications that increase the risk of bleeding.
        </p>`,
      },
      {
        id: 4,
        question: 'What should you consider after a treatment?',
        answer: `<p>Do not exercise or sunbathe for a few days after the treatment. Also avoid using makeup the same day as the treatment, and touch the treated area as little as possible.</p>`,
      },
      {
        id: 5,
        question: 'How long does a treatment last?',
        answer: `<p>How long a treatment lasts is individual and also depends on the area treated and the product used. Generally, fillers last about six to ten months. For Botox, the duration is about three to six months.</p>`,
      },
      {
        id: 6,
        question: 'Are there any clear trends in the industry right now?',
        answer: `<p>We see a clear trend towards a demand for natural results; it should not look injected.</p>`,
      },
      {
        id: 7,
        question: 'There are different brands - what is the difference?',
        answer: `<p>You should ask what is being injected into your body and be careful with your choices. The most well-known and safest is Restylane, a Swedish quality product. It contains very pure hyaluronic acid that is not mixed with many additives.</p>`,
      },
      {
        id: 8,
        question: 'Is the treatment dangerous? Does it hurt? Do you get anesthesia?',
        answer: `<p>Before a filler treatment, you are informed about risks such as bleeding, infection, or necrosis (tissue death). Necrosis is very rare among experienced practitioners.
          <br /><br />
          The treatment can be almost completely painless. You often get numbing cream before a treatment, but not everyone uses it.
        </p>`,
      },
    ],
  },
  {
    title: 'Common Questions About Eyelashes',
    path: '/faq-vanliga-fragor/kring-fransar',
    questions: [
      {
        id: 1,
        question: 'What is the difference between volume and single lashes?',
        answer: `<p>Volume lashes
          <br /><br />
          Volume lashes are the technique for those who want a clear result and more volume in the lashes. Simply put, a whole bunch of lashes is inserted, giving a fuller impression. This technique can give more than four hundred lashes per eye in addition to your natural lashes.
          <br /><br />
          Single lashes
          <br /><br />
          While volume lashes give more volume, single lashes give a more natural and longer contour. The number of lashes is fewer than with volume lashes; usually, there are fifty to a hundred per eye. The length, thickness, and shape of the single lashes are adapted to your eye.
        </p>`,
      },
      {
        id: 2,
        question: 'What should you consider before coming for an eyelash extension?',
        answer: `<p>Arrive on time for your eyelash extension and preferably without makeup. Is it your first time? Think about what result you want; you can, for example, bring a picture that illustrates what you are looking for—whether it is something very dramatic or a more natural look.</p>`,
      },
      {
        id: 3,
        question: 'What should you consider after a treatment?',
        answer: `<p>For best maintenance, it is very important to regularly clean the lashes with a lash shampoo. Otherwise, sebum, dirt, and bacteria easily accumulate along the lash line, which can cause you to lose lashes prematurely and, in the worst case, get an eye infection. You should not shower or wash your eyes/face twelve hours after an extension or refill due to the glue's curing time.
          <br /><br />
          Try not to "sleep on your face" and do not use oil near the lashes.
        </p>`,
      },
      {
        id: 4,
        question: 'How long does a treatment last?',
        answer: `<p>The durability after a treatment varies depending on how you take care of your lashes. But it is most common to come for a refill every three weeks. Eyelash extensions are also negatively affected by hormones, pregnancy, and stress.</p>`,
      },
      {
        id: 5,
        question: 'Can you do anything to maintain the lashes after a treatment?',
        answer: `<p>Clean the lashes daily with a suitable lash shampoo and brush</p>`,
      },
      {
        id: 6,
        question: 'Can you wear makeup as usual afterwards?',
        answer: `<p>You can absolutely wear makeup as usual after a treatment, but do not use mascara on the extensions.</p>`,
      },
      {
        id: 7,
        question: 'What is trendy in the industry right now?',
        answer: `<p>Trends in the industry vary in different cities, but more people want a more natural look than before. Mix and light volume are most popular. Working with different lengths and different curls in a set is also extra trendy.</p>`,
      },
    ],
  },
  {
    title: 'Common Questions About Skincare',
    path: '/faq-vanliga-fragor/kring-hudvard',
    questions: [
      {
        id: 1,
        question: 'Why should you book a skincare/facial treatment?',
        answer: `<p>
          <ul style="list-style:disc;margin-left: 24px;">
            <li>Over time, our cell renewal and production of collagen and elastin decrease, while our natural hyaluronic acid in the skin breaks down. To maintain the skin's firmness and elasticity, good skincare is important.</li>
            <li>Facial treatments can be adapted to a variety of skin types, needs, and problem areas and help you achieve healthy and well-being skin.</li>
            <li>Regular facial treatments with the right products and nutrients can help you achieve the best results for your skin.</li>
          </ul>
        </p>`,
      },
      {
        id: 2,
        question: 'What is meant by a classic facial treatment?',
        answer: `<p>The classic facial is a comprehensive treatment tailored to your needs. The treatment includes deep cleansing, steam, pore cleansing, and peeling followed by a face mask and facial massage. Products used during the treatment are adapted to your wishes and skin type. Problem areas to work with can include dry/oily skin, acne, or rosacea.</p>`,
      },
      {
        id: 3,
        question: 'What is an express facial?',
        answer: `<p>For those who are short on time but want quick results and the feeling of clean skin with new radiance, perfect before the weekend or upcoming festivities! The treatment includes light cleansing with steam, a shorter facial massage, and a face mask and products adapted for your skin type. </p>`,
      },
      {
        id: 4,
        question: 'What is anti-age?',
        answer: `<p>A treatment developed specifically for mature skin. As we age, our skin loses its firmness and elasticity, cell renewal slows down, and our collagen breaks down. In an anti-age treatment, products developed for aging skin are used; the products contain substances that stimulate cell formation, such as hyaluronic acid, which moisturizes and increases skin elasticity, as well as probiotics that combat aging processes. The treatment ends with an anti-age mask and moisturizing serum for radiance and moisturizing effect.</p>`,
      },
      {
        id: 5,
        question: 'What is dermapen microneedling?',
        answer: `<p>Dermapen/Microneedling is a new and revolutionary needle treatment that stimulates the skin's production of collagen. By using microneedling, nutrient-rich products with active ingredients can penetrate deeper into the skin to achieve a rejuvenating effect. The treatment is also used on problem areas such as wrinkles, large pores, sun-damaged skin, or visible scars on the face. The treatment can also be done together with a so-called skin booster with hyaluronic acid, which gives the skin a real vitamin injection and new radiance!</p>`,
      },
      {
        id: 6,
        question: 'What is chemical peeling?',
        answer: `<p>Chemical peeling is a collective term for treatments where a mixture of acids is used to achieve the desired result, e.g., AHA or TCA acids. This treatment form is effective for working with problem areas such as scars, rough skin, pimples, or pigmentation, but can also be used as a single treatment for extra refreshment. For extra radiance, treatments often end with a face mask containing both vitamins and antioxidants.</p>`,
      },
    ],
  },
  {
    title: 'Common Questions About Haircuts',
    path: '/faq-vanliga-fragor/kring-klippning',
    questions: [
      {
        id: 1,
        question: 'What types of treatments are most common?',
        answer: `<p>There are always strong trends in hairstyles. Which hairstyle suits you? It is always good to prepare for your visit to the hairdresser by looking at inspirational pictures online or in magazines to show the hairdresser. It is then easier for you to describe what you want and easier for the hairdresser to understand how you want your hairstyle.</p>`,
      },
      {
        id: 2,
        question: 'Short haircuts',
        answer: `<p>Cutting short hair is the most common service for men. The technique the hairdresser uses varies greatly. Some only use scissors, while others use a machine. Talk to your hairdresser before the visit about which method you want.</p>`,
      },
      {
        id: 3,
        question: 'Long haircuts',
        answer: `<p>It takes more time to cut long hair and is often combined with another treatment such as toning or highlights. This year, the trend is to cut so that you do not have to style so much yourself. We also see a trend towards a more androgynous style.</p>`,
      },
      {
        id: 4,
        question: 'Washing your hair at the hairdresser',
        answer: `<p>Even if you have already washed your hair at home or at the gym, there is nothing nicer than having your hair washed at the hairdresser. Many hairdressers also offer a scalp massage, which is wonderful.</p>`,
      },
      {
        id: 5,
        question: 'How often should I get a haircut?',
        answer: `<p>How often you should cut your hair depends on your hair quality and hairstyle. A short hairstyle usually needs to be cut every 3-8 weeks to maintain the desired length and shape. Long and healthy hair only needs to be cut every three months, while dry and damaged hair needs to be cut more often. Consult your hairdresser about what suits your hair type and style.</p>`,
      },
      {
        id: 6,
        question: 'How should I choose a hairdresser?',
        answer: `<p>Most of us look for a hairdresser who understands how we want our hairstyle, who we feel comfortable with, and who is located near our home or work. On bokadirekt.se, you can both find and book hairdressers near you and read ratings and reviews from previous customers before your visit.</p>`,
      },
      {
        id: 7,
        question: 'How should I care for my hair between visits to the hairdresser?',
        answer: `<p>To maintain healthy hair between visits, you need to give your hair the right conditions. Use a shampoo and conditioner suitable for your hair type, do a hair mask a couple of times a month for extra moisture, and use gentle styling tools. Talk to your hairdresser if you are unsure which products suit you.</p>`,
      },
      {
        id: 8,
        question: 'What are the hottest trends right now?',
        answer: `<p>In line with the pandemic year 2020, hair trends have been characterized by easy-care hairstyles and loose updos. Now, many new exciting hairstyles are expected to become popular. Curls, bangs in various forms, bold hair colors, and androgynous men's hairstyles will be seen more in 2021. </p>`,
      },
    ],
  },
  {
    title: 'Common Questions Chiropractic',
    path: '/faq-vanliga-fragor/kiropraktik',
    questions: [
      {
        id: 1,
        question: 'What is chiropractic?',
        answer: `<p>Chiropractic treatment is an adjusting treatment form primarily used to treat and prevent problems related to the spine. Problems related to the back can cause symptoms such as tension/lock in the shoulder area, misalignment, lumbago, sciatica, neck pain, and back pain. A chiropractor works to restore normal function in the patient's joints and muscles.
          <br /><br />
          During the treatment, the joints and muscles of the spine are examined to diagnose tension and possible injuries. The classic treatment is performed manually with the hands, but medical acupuncture and shockwave therapy can also be used as a complement.
          <br /><br />
          After treatment, a customized rehabilitation plan is usually given so that the patient can work preventively and long-term with their problems. A chiropractor can also give advice on diet, supplements, and daily exercise that can further help improve well-being.
        </p>`,
      },
      {
        id: 2,
        question: 'Is chiropractic treatment used for sports injuries?',
        answer: `<p>Yes, chiropractic treatment can also be used for sports injuries, both for acute injuries such as lumbago or for more long-term strain injuries caused by, for example, a misalignment in the back. Chiropractic treatment can also be used to prevent injuries and thus increase your performance level. Examples of injuries and problems often treated with chiropractic are tennis elbow, runner's knee, calf strain, and muscle tears. </p>`,
      },
    ],
  },
  {
    title: 'Common Questions About Massage Treatments',
    path: '/faq-vanliga-fragor/kring-massagebehandlingar',
    questions: [
      {
        id: 1,
        question: 'Why should you book a massage appointment?',
        answer: `<p>There are several benefits to massage.
          <ul style="list-style:disc;margin-left: 24px;">
            <li>A well-performed massage calms the nervous system and has a relaxing effect on the body. </li>
            <li>Massage reduces muscle tension and anxiety, which in the long run can even help prevent depression.</li>
            <li>Massage increases the body's blood circulation and oxygenates the body's cells</li>
          </ul>
        </p>`,
      },
      {
        id: 2,
        question: 'What is classic Swedish massage?',
        answer: `<p>The most common form of massage in Sweden is the traditional "Classic Swedish massage". The technique is based on kneading and stroking the skin and muscles. Sometimes devices are used in the technique to stimulate the muscles with vibrations. In addition to the general beneficial effects of massage, Classic Swedish massage is used to soften muscles, relieve muscle soreness, prevent muscle knots, and increase lymph flow.</p>`,
      },
      {
        id: 3,
        question: 'What is hot stone massage?',
        answer: `<p>Hot stone massage is a form of massage where the therapist uses warm stones and aromatic oils as tools. The heat from the lava stones contributes to a relaxing effect, and the method is best suited for relaxation and stress reduction. Minerals from the stones, such as iron, are also absorbed by the body with beneficial effects.</p>`,
      },
      {
        id: 4,
        question: 'What is tactile stimulation?',
        answer: `<p>Tactile massage is a gentler type of massage therapy where the body's receptors are touched with less force and lighter touch. The method is especially suitable for stress reduction and for counteracting stress.</p>`,
      },
      {
        id: 5,
        question: 'What is connective tissue massage?',
        answer: `<p>Connective tissue massage is a method for deep treatment of muscles. It is an intensive therapy that goes deep to break up previous scar tissue and release connective tissue. The movements in connective tissue massage are long and are traditionally done without massage oil or similar to create more friction and warm up the muscles.
          <br />
          The technique is normally used more to counteract muscle problems and can be somewhat more painful and less relaxing than other techniques that are more aimed at relaxation.
        </p>`,
      },
      {
        id: 6,
        question: 'What is sports massage?',
        answer: `<p>Sports massage is a therapeutic technique that originates from the world of sports. The treatment form is developed to help athletes' bodies recover. Many techniques in sports massage are taken from classic massage but are more therapeutic for, e.g., tennis elbow, shin splints, strains, tension headaches, mouse arm, etc., and are not of a relaxing nature.</p>`,
      },
      {
        id: 7,
        question: 'What is Thai massage?',
        answer: `<p>This massage, which unsurprisingly originates from Thailand, is an ancient technique that combines parts of classic acupressure and yoga into a treatment. The treatment form is therefore good for improving the body's mobility and posture. Traditionally, the technique is performed on the floor on a mat and usually covers the whole body. The therapist uses not only their hands but also elbows, knees, etc.
          <br /><br />
          The technique is both relaxing and partly therapeutic. Many feel both relaxed and more energetic after a treatment.
        </p>`,
      },
      {
        id: 8,
        question: 'What is acupressure?',
        answer: `<p>Acupressure originates from oriental medicine and is of a relaxing nature. The patient often keeps their clothes on during the treatment, and the relaxing treatment is done by pressing sensitive points around the neck, head, and shoulders.</p>`,
      },
      {
        id: 9,
        question: 'What is pregnancy massage?',
        answer: `<p>Pregnancy massage is designed to relieve common pains for pregnant women, such as problems with the back, lower back, and shoulders. The treatment is both relaxing and therapeutic and is done on a bench with a hole for the stomach or a specially designed cushion for the stomach. Massage oil adapted for pregnancy, free from substances that the fetus may be sensitive to, is often used.</p>`,
      },
      {
        id: 10,
        question: 'What trends are there in massage?',
        answer: `<p><strong>LPG or Lipo-massage</strong>
          <br /><br />
          LPG massage is a type of massage performed with an LPG machine. The technique reduces fat deposits in the body and is therefore seen as an alternative to liposuction and other surgical procedures. The technique also has similarities with connective tissue massage as it is deep and also has moisturizing properties that make the skin more elastic. During the treatment, the patient usually puts on a type of bodysuit that allows the LPG machine to glide better. Even if it may not sound like it, the treatment is pleasant and just as relaxing as a classic massage.
        </p>`,
      },
    ],
  },
  {
    title: 'Common Questions About Nail Treatments',
    path: '/faq-vanliga-fragor/kring-olika-behandling-for-naglar',
    questions: [
      {
        id: 1,
        question: 'Why should you book a nail treatment?',
        answer: `<p>
          <ul style="list-style:disc;margin-left: 24px;">
            <li>Well-groomed nails give beautiful hands and a well-cared-for impression.</li>
            <li>Let someone take care of your tired hands and feet and treat yourself to a relaxing moment in everyday life.</li>
            <li>Endless choices, materials, shapes, colors, and designs—there is a nail treatment for everyone!</li>
          </ul>
        </p>`,
      },
      {
        id: 2,
        question: 'What is nail extension?',
        answer: `<p>With a nail extension, your nails get a whole new look. Suitable for those with weak and brittle nails who want both longer and stronger nails. The nails are built up with a tip that is then reinforced with material in the form of acrylic or gel. Acrylic, which is the strongest material, is applied with a powder technique where the acrylic hardens from the oxygen in the air. When using gel, the material is applied to the nail and then cured under a UV lamp. With both techniques, the nails are shaped to the desired form and then painted in any color and design. A nail extension gives lasting results for up to 6 weeks.</p>`,
      },
      {
        id: 3,
        question: 'What is gel polish/shellac?',
        answer: `<p>Gel polish/Shellac is a mixture of nail polish and gel and is a permanent polish suitable for those with naturally strong nails. The polish, which comes in a variety of colors and shades, is applied to your own nails and hardens under a UV lamp. The treatment is gentle and provides a protective and durable result for up to 4 weeks. </p>`,
      },
      {
        id: 4,
        question: 'What is a pedicure?',
        answer: `<p>A pedicure includes a foot bath and a relaxing lower leg massage and is the perfect treatment to refresh tired feet. Calluses on the feet are removed, heels are filed, nails are cut and filed to the desired length and shape. The pedicure ends with polishing, where a permanent shellac can be used for lasting results for up to 6 weeks. The pedicure can also be done as a quicker express treatment, perfect for those on the go! </p>`,
      },
    ],
  },
];

export const faqFin: Section[] = [
  {
    title: 'Yleiset kysymykset Akupunktio',
    path: '/faq-vanliga-fragor/akupunktur',
    questions: [
      {
        id: 1,
        question: 'Mitä on akupunktio?',
        answer: `<p>Akupunktio on vaihtoehtoinen hoitomuoto, joka perustuu perinteiseen kiinalaiseen lääketieteeseen. Kiinalaisessa lääketieteessä ihmistä ja kehoa tarkastellaan kokonaisuutena, ja hoitojen tavoitteena on palauttaa kehon oma tasapaino. Nykyään akupunktiota käytetään hoitomuotona myös suuressa osassa läntistä maailmaa. Perinteisesti akupunktion ajatellaan vaikuttavan kehon meridiaaneihin, jotka muodostavat kiinalaisen lääketieteen perustan. Akupunktiohoidossa ohuita neuloja asetetaan ihon akupunktiopisteisiin, ja näitä stimuloimalla pyritään poistamaan kehon hermoston tukoksia ja epätasapainoa. Hoito koetaan rauhoittavana ja kivunlievitystä tuovana.
          <br /> <br />
          Hoidon aikana ohuita neuloja asetetaan ihoon kipualueen ympärille. Yleensä neuloja pidetään paikallaan 20–30 minuuttia ennen niiden poistamista. Joissain tapauksissa neuloihin voidaan liittää sähköstimulaatio, jota kutsutaan elektroakupunktioksi. Klassista akupunktiota käytetään monenlaisten vaivojen, kuten stressin, jännityspäänsäryn, uupumuksen, ahdistuksen, lihaskipujen ja PMS-oireiden hoitoon.
        </p>`,
      },
      {
        id: 2,
        question: 'Miksi varata aika akupunktioon?',
        answer: `<p>
          <ul style="list-style:disc;margin-left: 24px;">
            <li>Akupunktiohoito voi lievittää kipua esimerkiksi jännityspäänsäryssä, iskiasoireissa, synnytyskivuissa ja PMS-oireissa.</li>
            <li>Hoito voi myös rauhoittaa stressin, ahdistuksen ja lievän masennuksen oireita.</li>
          </ul>
        </p>`,
      },
    ],
  },
  {
    title: 'Yleiset kysymykset Fillereistä',
    path: '/faq-vanliga-fragor/fillers',
    questions: [
      {
        id: 1,
        question: 'Miksi tehdään injektiohoitoja?',
        answer: `<p>Ehkä tunnet menettäneesi volyymia tai haluat vain parantaa jotakin ryppyä. Tärkeintä on, että haluat hoidon itse, etkä siksi että joku muu on kehottanut siihen.</p>`,
      },
      {
        id: 2,
        question: 'Mitkä ovat yleisimmät harhaluulot botoxista/fillereistä/injektioista?',
        answer: `<p>Yleinen harhaluulo on, että hoito muuttaa ulkonäköäsi niin paljon, ettei sinua tunnisteta. Näin ei yleensä ole. Monet ottavat esimerkiksi täytettä huuliin vain palauttaakseen hieman volyymia, tasoittaakseen huulia tai lisätäkseen kosteutta.</p>`,
      },
      {
        id: 3,
        question: 'Mitä tulee ottaa huomioon ennen filler-hoitoa?',
        answer: `<p>Valitse hoitaja, joka on vähintään laillistettu sairaanhoitaja ja koulutettu esteettisten injektioiden neuvostossa, mielellään usean vuoden kokemuksella.
          <br /><br />
          Käy konsultaatiossa ennen hoitoa ja arvioi, onko klinikka luotettava.
          <br /><br />
          Noin kaksi viikkoa ennen hoitoa ei tule käyttää lääkkeitä, jotka lisäävät verenvuotoriskiä.
        </p>`,
      },
      {
        id: 4,
        question: 'Mitä tulee ottaa huomioon hoidon jälkeen?',
        answer: `<p>Vältä liikuntaa ja auringonottoa muutaman päivän ajan hoidon jälkeen. Älä myöskään käytä meikkiä samana päivänä ja vältä koskemasta hoidettuun alueeseen mahdollisimman paljon.</p>`,
      },
      {
        id: 5,
        question: 'Kuinka kauan hoidon vaikutus kestää?',
        answer: `<p>Hoidon kesto on yksilöllistä ja riippuu myös hoidettavasta alueesta ja käytetystä aineesta. Yleisesti filler-hoito kestää noin 6–10 kuukautta. Botox-hoidon vaikutus kestää noin 3–6 kuukautta.</p>`,
      },
      {
        id: 6,
        question: 'Onko alalla tällä hetkellä selkeitä trendejä?',
        answer: `<p>Selkeä trendi on luonnollisten tulosten kysyntä – lopputuloksen ei tule näyttää keinotekoiselta.</p>`,
      },
      {
        id: 7,
        question: 'Onko eri tuotemerkeillä eroja?',
        answer: `<p>Kysy aina, mitä kehoosi ruiskutetaan ja ole tarkka valinnoissasi. Tunnetuin ja turvallisin on ruotsalainen Restylane, joka sisältää erittäin puhdasta hyaluronihappoa ilman ylimääräisiä lisäaineita.</p>`,
      },
      {
        id: 8,
        question: 'Onko hoito vaarallista? Sattuuko se? Saako puudutusta?',
        answer: `<p>Ennen filler-hoitoa kerrotaan riskeistä, kuten verenvuodosta, infektiosta tai nekroosista (kudoksen kuolio). Nekroosi on kuitenkin hyvin harvinaista kokeneilla hoitajilla.
          <br /><br />
          Hoito voi olla lähes kivuton. Usein käytetään puudutusvoidetta ennen hoitoa, mutta kaikki eivät käytä sitä.
        </p>`,
      },
    ],
  },
  {
    title: 'Yleiset kysymykset Ripsistä',
    path: '/faq-vanliga-fragor/kring-fransar',
    questions: [
      {
        id: 1,
        question: 'Mikä ero on volyymi- ja klassisilla ripsillä?',
        answer: `<p>Volyymiripset
          <br /><br />
          Volyymiripset sopivat sinulle, joka haluat selvästi näkyvän lopputuloksen ja enemmän tuuheutta ripsiin. Käytännössä asetetaan koko nippu ripsiä, mikä antaa täyteläisemmän vaikutelman. Tällä tekniikalla voidaan lisätä yli 400 ripseä per silmä luonnollisten ripsien lisäksi.
          <br /><br />
          Klassiset ripset
          <br /><br />
          Volyymiripset antavat enemmän tuuheutta, kun taas klassiset ripset antavat luonnollisemman ja pidemmän ilmeen. Ripsien määrä on pienempi kuin volyymiripsissä; yleensä 50–100 per silmä. Ripsien pituus, paksuus ja muoto räätälöidään silmäsi mukaan.
        </p>`,
      },
      {
        id: 2,
        question: 'Mitä tulee huomioida ennen ripsienpidennystä?',
        answer: `<p>Tule ajoissa ja mieluiten ilman meikkiä. Jos olet ensimmäistä kertaa, mieti millaista lopputulosta haluat – voit ottaa mukaan kuvan, joka havainnollistaa toivettasi, olipa kyseessä dramaattinen tai luonnollinen look.</p>`,
      },
      {
        id: 3,
        question: 'Mitä tulee huomioida hoidon jälkeen?',
        answer: `<p>Parhaan lopputuloksen saavuttamiseksi on tärkeää puhdistaa ripset säännöllisesti ripsishampoolla. Muuten talia, likaa ja bakteereja kertyy helposti ripsirajaan, mikä voi aiheuttaa ripsien ennenaikaista irtoamista ja pahimmillaan silmätulehduksen. Älä suihkuta tai pese kasvoja/silmiä 12 tuntiin pidennyksen tai huollon jälkeen liiman kovettumisen vuoksi.
          <br /><br />
          Vältä nukkumista kasvot tyynyä vasten ja älä käytä öljyä ripsien läheisyydessä.
        </p>`,
      },
      {
        id: 4,
        question: 'Kuinka kauan pidennys kestää?',
        answer: `<p>Kesto vaihtelee sen mukaan, miten hoidat ripsiäsi. Yleensä huoltoon tullaan kolmen viikon välein. Ripsienpidennykseen vaikuttavat myös hormonit, raskaus ja stressi.</p>`,
      },
      {
        id: 5,
        question: 'Voiko ripsiä huoltaa hoidon jälkeen?',
        answer: `<p>Puhdista ripset päivittäin sopivalla ripsishampoolla ja harjalla.</p>`,
      },
      {
        id: 6,
        question: 'Voiko meikata normaalisti hoidon jälkeen?',
        answer: `<p>Voit meikata normaalisti hoidon jälkeen, mutta älä käytä ripsiväriä pidennyksiin.</p>`,
      },
      {
        id: 7,
        question: 'Mikä on trendikästä ripsialalla juuri nyt?',
        answer: `<p>Trendit vaihtelevat kaupungeittain, mutta yhä useampi haluaa luonnollisemman ilmeen. Mix ja kevyt volyymi ovat suosituimpia. Myös eripituisten ja -kaarevuisten ripsien yhdistely on erityisen trendikästä.</p>`,
      },
    ],
  },
  {
    title: 'Yleiset kysymykset Ihonhoidosta',
    path: '/faq-vanliga-fragor/kring-hudvard',
    questions: [
      {
        id: 1,
        question: 'Miksi varata aika kasvohoitoon/ihonhoitoon?',
        answer: `<p>
          <ul style="list-style:disc;margin-left: 24px;">
            <li>Solujen uusiutuminen ja kollageenin sekä elastaanin tuotanto vähenevät ajan myötä, ja ihon luonnollinen hyaluronihappo hajoaa. Hyvä ihonhoito auttaa ylläpitämään ihon kimmoisuutta ja joustavuutta.</li>
            <li>Kasvohoidot voidaan räätälöidä monenlaisille ihotyypeille, tarpeille ja ongelma-alueille ja ne auttavat saavuttamaan terveen ja hyvinvoivan ihon.</li>
            <li>Säännölliset kasvohoidot oikeilla tuotteilla ja ravintoaineilla auttavat saavuttamaan parhaan tuloksen juuri sinun ihollesi.</li>
          </ul>
        </p>`,
      },
      {
        id: 2,
        question: 'Mitä tarkoitetaan klassisella kasvohoidolla?',
        answer: `<p>Klassinen kasvohoito on laaja hoito, joka räätälöidään tarpeidesi mukaan. Hoito sisältää syväpuhdistuksen, höyryn, huokosten puhdistuksen ja kuorinnan, jonka jälkeen tehdään kasvonaamio ja kasvohieronta. Käytettävät tuotteet valitaan toiveidesi ja ihotyyppisi mukaan. Ongelmakohtia voivat olla esimerkiksi kuiva/rasvainen iho, akne tai ruusufinni.</p>`,
      },
      {
        id: 3,
        question: 'Mitä on express-kasvohoito?',
        answer: `<p>Sopii sinulle, jolla on vähän aikaa mutta haluat nopeasti puhtaan ja hehkuvan ihon – täydellinen ennen viikonloppua tai juhlia! Hoito sisältää kevyen puhdistuksen höyryllä, lyhyen kasvohieronnan sekä kasvonaamion ja ihollesi sopivat tuotteet.</p>`,
      },
      {
        id: 4,
        question: 'Mitä on anti age -hoito?',
        answer: `<p>Hoito on kehitetty erityisesti ikääntyvälle iholle. Iän myötä iho menettää kimmoisuuttaan ja joustavuuttaan, solujen uusiutuminen hidastuu ja kollageeni hajoaa. Anti age -hoidossa käytetään tuotteita, jotka stimuloivat solujen muodostumista, kuten hyaluronihappoa, joka kosteuttaa ja lisää ihon joustavuutta, sekä probiootteja, jotka hidastavat ikääntymisprosessia. Hoito viimeistellään anti age -naamiolla ja kosteuttavalla seerumilla.</p>`,
      },
      {
        id: 5,
        question: 'Mitä on dermapen-mikroneulaus?',
        answer: `<p>Dermapen/mikroneulaus on uusi ja mullistava hoitomuoto, joka stimuloi ihon kollageenintuotantoa. Neulaushoidolla ravinteikkaat ja aktiiviset ainesosat pääsevät syvemmälle ihoon ja aikaansaavat nuorentavan vaikutuksen. Hoitoa käytetään myös ongelma-alueille, kuten ryppyihin, suuriin huokosiin, auringon vaurioittamaan ihoon tai näkyviin arpiin kasvoissa. Hoito voidaan tehdä myös yhdessä ns. skinboosterin kanssa, joka sisältää hyaluronihappoa ja antaa iholle vitamiiniruiskeen ja uutta hehkua!</p>`,
      },
      {
        id: 6,
        question: 'Mitä on kemiallinen kuorinta?',
        answer: `<p>Kemiallinen kuorinta on yleisnimitys hoidoille, joissa käytetään happosekoituksia, kuten AHA- tai TCA-happoja, halutun tuloksen saavuttamiseksi. Hoito sopii esimerkiksi arpien, karhean ihon, finnien tai pigmenttimuutosten hoitoon, mutta sitä voidaan käyttää myös yksittäisenä virkistävänä hoitona. Lisähehkua varten hoito viimeistellään usein kasvonaamiolla, joka sisältää vitamiineja ja antioksidantteja.</p>`,
      },
    ],
  },
  {
    title: 'Yleiset kysymykset Hiustenleikkauksesta',
    path: '/faq-vanliga-fragor/kring-klippning',
    questions: [
      {
        id: 1,
        question: 'Mitkä hoitotyypit ovat yleisimpiä?',
        answer: `<p>Hiustyyleissä on aina vahvoja trendejä. Mikä tyyli sopii sinulle? On hyvä valmistautua kampaajakäyntiin etsimällä inspiraatiokuvia netistä tai lehdistä ja näyttää ne kampaajalle. Näin sinun on helpompi kertoa toiveesi ja kampaajan ymmärtää, mitä haluat.</p>`,
      },
      {
        id: 2,
        question: 'Lyhyiden hiusten leikkaus',
        answer: `<p>Lyhyiden hiusten leikkaus on yleisin palvelu miehille. Kampaajat käyttävät erilaisia tekniikoita – jotkut leikkaavat vain saksilla, toiset koneella. Keskustele kampaajasi kanssa ennen käyntiä, mikä menetelmä sopii sinulle.</p>`,
      },
      {
        id: 3,
        question: 'Pitkien hiusten leikkaus',
        answer: `<p>Pitkien hiusten leikkaus vie enemmän aikaa ja yhdistetään usein muihin hoitoihin, kuten sävytykseen tai raitojen tekoon. Tänä vuonna trendinä on leikata niin, ettei hiuksia tarvitse itse muotoilla paljon. Myös androgyynit tyylit ovat nousussa.</p>`,
      },
      {
        id: 4,
        question: 'Hiusten pesu kampaajalla',
        answer: `<p>Vaikka olisit pessyt hiuksesi kotona tai salilla, mikään ei voita kampaajalla pesua. Monet kampaajat tarjoavat myös päänahan hierontaa, mikä on ihanaa.</p>`,
      },
      {
        id: 5,
        question: 'Kuinka usein hiukset tulisi leikata?',
        answer: `<p>Leikkausväli riippuu hiustesi laadusta ja tyylistä. Lyhyt kampaus tarvitsee yleensä leikkauksen 3–8 viikon välein, jotta muoto ja pituus säilyvät. Pitkät ja terveet hiukset riittää leikata kolmen kuukauden välein, kun taas kuivat ja vaurioituneet hiukset kannattaa leikata useammin. Kysy kampaajaltasi, mikä sopii sinun hiustyypillesi ja kampauksellesi.</p>`,
      },
      {
        id: 6,
        question: 'Miten valita kampaaja?',
        answer: `<p>Suurin osa etsii kampaajaa, joka ymmärtää toiveesi, jonka kanssa viihdyt ja joka sijaitsee lähellä kotia tai työpaikkaa. Bokadirekt.se:stä löydät ja voit varata kampaajia läheltäsi sekä lukea arvosteluja ennen käyntiäsi.</p>`,
      },
      {
        id: 7,
        question: 'Miten hoitaa hiuksia kampaamokäyntien välillä?',
        answer: `<p>Hyvinvoivien hiusten ylläpitämiseksi tulee käyttää hiustyypille sopivaa shampoota ja hoitoainetta, tehdä hiusnaamio muutaman kerran kuukaudessa ja käyttää hellävaraisia muotoiluvälineitä. Kysy kampaajaltasi, jos olet epävarma, mitkä tuotteet sopivat sinulle.</p>`,
      },
      {
        id: 8,
        question: 'Mitkä ovat kuumimmat trendit juuri nyt?',
        answer: `<p>Pandemiavuoden 2020 jälkeen hiustrendit ovat olleet helppohoitoisia kampauksia ja rentoja nutturoita. Nyt odotetaan monia uusia, jännittäviä tyylejä. Kiharat, erilaiset otsatukat, rohkeat värit ja androgyynit miesten kampaukset ovat vuoden 2021 trendejä.</p>`,
      },
    ],
  },
  {
    title: 'Yleiset kysymykset Kiropraktiikasta',
    path: '/faq-vanliga-fragor/kiropraktik',
    questions: [
      {
        id: 1,
        question: 'Mitä on kiropraktiikka?',
        answer: `<p>Kiropraktinen hoito on manipuloiva hoitomuoto, jota käytetään ensisijaisesti selkärankaan liittyvien ongelmien hoitoon ja ennaltaehkäisyyn. Selkäongelmat voivat aiheuttaa oireita, kuten jännitystä/ lukkoja hartioissa, virheasentoja, noidannuolen, iskiasoireita, niskakipuja ja selkäkipuja. Kiropraktikko pyrkii palauttamaan nivelten ja lihasten normaalin toiminnan.
          <br /><br />
          Hoidon aikana tutkitaan selkärangan nivelet ja lihakset jännitysten ja mahdollisten vaurioiden diagnosoimiseksi. Klassinen hoito tehdään käsin, mutta myös lääketieteellistä akupunktiota ja iskuaaltohoitoa voidaan käyttää täydentävinä hoitoina.
          <br /><br />
          Hoidon jälkeen annetaan usein yksilöllinen kuntoutussuunnitelma, jotta potilas voi itse ehkäistä ja hoitaa vaivojaan pitkäjänteisesti. Kiropraktikko voi myös antaa neuvoja ravinnosta, lisäravinteista ja arkiaktiivisuudesta hyvinvoinnin tukemiseksi.
        </p>`,
      },
      {
        id: 2,
        question: 'Käytetäänkö kiropraktiikkaa urheiluvammoissa?',
        answer: `<p>Kyllä, kiropraktista hoitoa voidaan käyttää myös urheiluvammoissa, sekä akuuteissa kuten noidannuoli että pitkäaikaisissa rasitusvammoissa, jotka johtuvat esimerkiksi selän virheasennosta. Kiropraktiikkaa voidaan käyttää myös vammojen ennaltaehkäisyyn ja suorituskyvyn parantamiseen. Esimerkkejä vaivoista, joita hoidetaan kiropraktiikalla, ovat tenniskyynärpää, juoksijan polvi, pohjekrampit ja lihasrepeämät.</p>`,
      },
    ],
  },
  {
    title: 'Yleiset kysymykset Hierontahoidoista',
    path: '/faq-vanliga-fragor/kring-massagebehandlingar',
    questions: [
      {
        id: 1,
        question: 'Miksi varata aika hierontaan?',
        answer: `<p>Hieronnalla on monia hyötyjä.
          <ul style="list-style:disc;margin-left: 24px;">
            <li>Hyvin tehty hieronta rauhoittaa hermostoa ja rentouttaa kehoa.</li>
            <li>Hieronta vähentää lihasjännitystä ja huolta, mikä voi pitkällä aikavälillä ehkäistä jopa masennusta.</li>
            <li>Hieronta lisää verenkiertoa ja hapettaa kehon soluja.</li>
          </ul>
        </p>`,
      },
      {
        id: 2,
        question: 'Mitä on klassinen ruotsalainen hieronta?',
        answer: `<p>Ruotsissa yleisin hierontamuoto on perinteinen klassinen hieronta. Tekniikka perustuu ihon ja lihasten vaivaamiseen ja sivelyyn. Joskus käytetään laitteita, jotka stimuloivat lihaksia värinällä. Klassista hierontaa käytetään lihasten pehmentämiseen, lihaskipujen lievittämiseen, lihasjumien ehkäisyyn ja lymfakierron lisäämiseen.</p>`,
      },
      {
        id: 3,
        question: 'Mitä on hot stone -hieronta?',
        answer: `<p>Hot stone -hieronnassa käytetään lämpimiä kiviä ja aromaattisia öljyjä. Lämpö rentouttaa ja menetelmä sopii parhaiten rentoutumiseen ja stressin lievitykseen. Kivistä, kuten laavakivistä, vapautuvat mineraalit, kuten rauta, imeytyvät kehoon ja tuovat hyvinvointia.</p>`,
      },
      {
        id: 4,
        question: 'Mitä on taktiilinen stimulointi?',
        answer: `<p>Taktiilinen hieronta on hellävaraisempi hierontamuoto, jossa kehon reseptoreita kosketetaan kevyesti. Menetelmä sopii erityisesti stressin lievitykseen.</p>`,
      },
      {
        id: 5,
        question: 'Mitä on sidekudoshieronta?',
        answer: `<p>Sidekudoshieronta on syväkudoshierontaa, jossa pyritään hajottamaan arpikudosta ja vapauttamaan sidekudosta. Liikkeet ovat pitkiä ja tehdään yleensä ilman hierontaöljyä, jotta syntyy enemmän kitkaa ja lihakset lämpenevät.
          <br />
          Tekniikkaa käytetään yleensä lihasongelmien hoitoon ja se voi olla kivuliaampaa ja vähemmän rentouttavaa kuin muut rentouttavat hierontamuodot.
        </p>`,
      },
      {
        id: 6,
        question: 'Mitä on urheiluhieronta?',
        answer: `<p>Urheiluhieronta on hoitotekniikka, joka on kehitetty urheilijoiden kehon palautumisen tueksi. Monet tekniikat ovat peräisin klassisesta hieronnasta, mutta urheiluhieronta on suunnattu enemmän esimerkiksi tenniskyynärpään, penikkataudin, venähdysten, jännityspäänsäryn ja hiirikäden hoitoon eikä ole rentouttavaa.</p>`,
      },
      {
        id: 7,
        question: 'Mitä on thaihieronta?',
        answer: `<p>Thaihieronta on Thaimaasta peräisin oleva ikivanha tekniikka, jossa yhdistyy klassista akupainantaa ja joogaa. Hoito parantaa liikkuvuutta ja ryhtiä. Perinteisesti hoito tehdään lattialla matolla ja käsittää koko kehon. Hieroja käyttää käsiensä lisäksi myös kyynärpäitä, polvia jne.
          <br /><br />
          Tekniikka on sekä rentouttava että osittain terapeuttinen. Monet tuntevat olonsa sekä rentoutuneeksi että virkistyneeksi hoidon jälkeen.
        </p>`,
      },
      {
        id: 8,
        question: 'Mitä on akupainanta?',
        answer: `<p>Akupainanta on peräisin itämaisesta lääketieteestä ja on rentouttava hoitomuoto. Usein potilas pitää vaatteet päällä ja hoito tehdään painamalla herkkiä pisteitä niskan, pään ja hartioiden alueella.</p>`,
      },
      {
        id: 9,
        question: 'Mitä on raskaushieronta?',
        answer: `<p>Raskaushieronta on kehitetty lievittämään raskaana olevien naisten yleisiä kipuja, kuten selkä-, alaselkä- ja hartiakipuja. Hoito on sekä rentouttavaa että terapeuttista ja tehdään pöydällä, jossa on reikä vatsalle tai erityisellä vatsatyynyllä. Käytetään usein raskaana oleville sopivaa hierontaöljyä, joka ei sisällä sikiölle haitallisia aineita.</p>`,
      },
      {
        id: 10,
        question: 'Mitkä ovat hieronnan trendit?',
        answer: `<p><strong>LPG- tai lipo-hieronta</strong>
          <br /><br />
          LPG-hieronta tehdään LPG-laitteella. Tekniikka vähentää rasvakertymiä ja nähdään vaihtoehtona rasvaimulle ja muille kirurgisille toimenpiteille. Tekniikka muistuttaa myös sidekudoshierontaa, sillä se on syväkudoksista ja kosteuttaa ihoa tehden siitä joustavamman. Hoidon aikana käytetään yleensä erityistä asua, jotta laite liukuu paremmin. Vaikka se ei kuulosta siltä, hoito on miellyttävä ja yhtä rentouttava kuin klassinen hieronta.
        </p>`,
      },
    ],
  },
  {
    title: 'Yleiset kysymykset Kynsihoidoista',
    path: '/faq-vanliga-fragor/kring-olika-behandling-for-naglar',
    questions: [
      {
        id: 1,
        question: 'Miksi varata aika kynsihoitoon?',
        answer: `<p>
          <ul style="list-style:disc;margin-left: 24px;">
            <li>Hyvin hoidetut kynnet tekevät käsistä kauniit ja antavat huolitellun vaikutelman.</li>
            <li>Anna jonkun hoitaa väsyneitä käsiäsi ja jalkojasi ja hemmottele itseäsi rentouttavalla hetkellä arjessa.</li>
            <li>Lukemattomia vaihtoehtoja – materiaaleja, muotoja, värejä ja designia – jokaiselle löytyy sopiva kynsihoito!</li>
          </ul>
        </p>`,
      },
      {
        id: 2,
        question: 'Mitä on kynsien pidennys?',
        answer: `<p>Kynsien pidennyksellä saat täysin uuden ilmeen kynsillesi. Sopii heikoille ja hauraille kynsille, jotka kaipaavat pituutta ja vahvuutta. Kynnet rakennetaan tipillä, joka vahvistetaan akryyli- tai geelillä. Akryyli on vahvin materiaali ja kovettuu ilman hapen vaikutuksesta. Geeli kovetetaan UV-lampulla. Molemmilla tekniikoilla kynnet muotoillaan haluttuun muotoon ja lakataan haluamallasi värillä ja designilla. Kynsien pidennys kestää jopa 6 viikkoa.</p>`,
      },
      {
        id: 3,
        question: 'Mitä on geelilakkaus/shellac?',
        answer: `<p>Geelilakkaus/Shellac on kynsilakan ja geelin sekoitus ja pysyvä lakkaus, joka sopii luonnostaan vahvoille kynsille. Lakkaa on monissa väreissä ja sävyissä, ja se kovetetaan UV-lampulla. Hoito on hellävarainen ja antaa suojaavan ja kestävän lopputuloksen jopa 4 viikoksi.</p>`,
      },
      {
        id: 4,
        question: 'Mitä on jalkahoito (pedikyyri)?',
        answer: `<p>Pedikyyri sisältää jalkakylvyn ja rentouttavan pohjehieronnan ja on täydellinen hoito väsyneille jaloille. Kovettumat poistetaan, kantapäät viilataan, kynnet leikataan ja viilataan haluttuun pituuteen ja muotoon. Pedikyyri viimeistellään lakkauksella, jossa voidaan käyttää pysyvää shellacia jopa 6 viikon kestävyyden saavuttamiseksi. Pedikyyri voidaan tehdä myös nopeana express-hoitona, joka sopii kiireiselle!</p>`,
      },
    ],
  },
];
